import { useContext, useEffect } from "react";
import { API_Version, ApiVersionInfo, ExportURL, ResultObj, allFetch, getControllerApiVersion } from "../Domain";
import { ApiContext, useApi } from "../apiProvider";
import { DateTimeFormate, dotNet6DateTimeToDefaulFormat, dotNet6DateToDefaulFormat, defaultDateTimeToDotNet6Format, defaultRangeDateToDotNet6Format } from "../function";

// StockApi的Hooks使用方式
export const useWorkOrderApi = () => {
    const apiManagerName = "workorderApiManager";
    const context = useContext(ApiContext);
    if (!context) {
        throw new Error("useWorkOrderApi must be used within a ApiProvider");
    }

    let { apiManagers, registerApiManager } = context;
    useEffect(() => {
        if (!apiManagers[apiManagerName]) {
            const newWorkOrderApiManager = new WorkOrderApiManager();
            newWorkOrderApiManager.init().then((apiManager) => {
                registerApiManager(apiManagerName, apiManager);
            }).catch((e) => {
                console.error("Error initializing API Manager", e);
            });
        }
    }, [apiManagers[apiManagerName]]);

    return useApi<WorkOrderApiManager>(apiManagerName);
};

// WorkOrderApiInterface 的定義
export interface WorkOrderApiInterface {
    /** 工單建立 */
    WorkOrderMasterCreate: (obj: WorkOrderMasterCreateParams) => Promise<ResultObj>
    /** 工單查詢 */
    WorkOrderMasterSearch: (obj: WorkOrderMasterSearchParams) => Promise<ResultObj<WorkOrderMasterSearchResItem[]>>
    /** 工單細項查詢 */
    WorkOrderMasterDetail: (obj: WorkOrderMasterDetailParams) => Promise<ResultObj<WorkOrderMasterDetailResItem>>
    /** 工單完工取消 */
    WorkOrderMasterCompleteCancel: (obj: WorkOrderMasterCompleteCancelParams) => Promise<ResultObj>
    /** 工單結案取消 */
    WorkOrderMasterCaseCloseCancel: (obj: WorkOrderMasterCaseCloseCancelParams) => Promise<ResultObj>
    /** 工單修改 */
    WorkOrderMasterUpdate: (obj: WorkOrderMasterUpdateParams) => Promise<ResultObj>
    /** 工單交修項目新建 */
    WorkOrderTurnoverRepairDetailCreate: (obj: WorkOrderTurnoverRepairDetailCreateParams) => Promise<ResultObj>
    /** 工單交修項目查詢 */
    WorkOrderTurnoverRepairDetailSearch: (obj: WorkOrderTurnoverRepairDetailSearchParams) => Promise<ResultObj>
    /** 工單交修項目修改 */
    WorkOrderTurnoverRepairDetailUpdate: (obj: WorkOrderTurnoverRepairDetailUpdateParams) => Promise<ResultObj>
    /** 工單交修項目刪除 */
    WorkOrderTurnoverRepairDetailDelete: (obj: WorkOrderTurnoverRepairDetailDeleteParams) => Promise<ResultObj>
    /** 工單作廢 */
    WorkOrderMasterInvalid: (obj: WorkOrderMasterInvalidParams) => Promise<ResultObj>
    /** 工單付款記錄查詢 */
    WorkOrderPayLogSearch: (obj: WorkOrderPayLogSearchParams) => Promise<ResultObj<WorkOrderPayLogSearchResItem[]>>
    /** 工單付款記錄新增 */
    WorkOrderPayLogCreate: (obj: WorkOrderPayLogCreateParams) => Promise<ResultObj>
    /** 工單付款記錄修改 */
    WorkOrderPayLogUpdate: (obj: WorkOrderPayLogUpdateParams) => Promise<ResultObj>
    /** 工單付款記錄刪除 */
    WorkOrderPayLogDelete: (obj: WorkOrderPayLogDeleteParams) => Promise<ResultObj>
    /** 新建付款紀錄細項 */
    WorkOrderPayLogDetailCreate: (obj: WorkOrderPayLogDetailCreateParams) => Promise<ResultObj>
    /** 刪除付款紀錄細項 */
    WorkOrderPayLogDetailDelete: (obj: WorkOrderPayLogDetailDeleteParams) => Promise<ResultObj>
    /** 查詢付款紀錄細項 */
    WorkOrderPayLogDetailSearch: (obj: WorkOrderPayLogDetailSearchParams) => Promise<ResultObj<WorkOrderPayLogDetailSearchResItem[]>>
    /** 完成工單外帳單(A單) */
    WorkOrderExtFinish: (obj: WorkOrderExtFinishParams) => Promise<ResultObj>
    /** 取消完成工單外帳單(A單) */
    WorkOrderExtFinishCancel: (obj: WorkOrderExtFinishCancelParams) => Promise<ResultObj>
    /** 工單完工 */
    WorkOrderMasterComplete: (obj: WorkOrderMasterCompleteParams) => Promise<ResultObj>
    /** 工單結案 */
    WorkOrderMasterCaseClose: (obj: WorkOrderMasterCaseCloseParams) => Promise<ResultObj>
    /** 工單庫存領用新建 */
    WorkOrderStockRequisitionCreate: (obj: WorkOrderStockRequisitionCreateParams) => Promise<ResultObj>
    /** 工單庫存領用查詢 */
    WorkOrderStockRequisitionSearch: (obj: WorkOrderStockRequisitionSearchParams) => Promise<ResultObj>
    /** 工單庫存領用修改 */
    WorkOrderStockRequisitionUpdate: (obj: WorkOrderStockRequisitionUpdateParams) => Promise<ResultObj>
    /** 紀錄工單庫存領用列印時間 */
    WorkOrderStockRequisitionRecordPrintTime: (obj: WorkOrderStockRequisitionRecordPrintTimeParams) => Promise<ResultObj>
    /** 失效工單庫存領用細項 */
    WorkOrderStockRequisitionInvalid: (obj: WorkOrderStockRequisitionInvalidParams) => Promise<ResultObj>
    /** 工單庫存領用刪除 */
    WorkOrderStockRequisitionDelete: (obj: WorkOrderStockRequisitionDeleteParams) => Promise<ResultObj>
    /** 維修清單細項新建 */
    RepairListDetailCreate: (obj: RepairListDetailCreateParams) => Promise<ResultObj>
    /** 維修清單細項查詢 */
    RepairListDetailSearch: (obj: RepairListDetailSearchParams) => Promise<ResultObj<RepairListDetailSearchResItem[]>>
    /** 維修清單檢查 */
    RepairListDetailCheck: (obj: RepairListDetailCheckParams) => Promise<ResultObj<RepairListDetailCheckResItem>>
    /** 維修清單細項修改 */
    RepairListDetailUpdate: (obj: RepairListDetailUpdateParams) => Promise<ResultObj>
    /** 維修清單細項刪除 */
    RepairListDetailDelete: (obj: RepairListDetailDeleteParams) => Promise<ResultObj>
    /** 維修清單們派工 */
    RepairListDetailDispatch: (obj: RepairListDetailDispatchParams) => Promise<ResultObj>
    /** 維修清單們開工 */
    RepairListDetailDispatchConstruct: (obj: RepairListDetailDispatchConstructParams) => Promise<ResultObj>
    /** 維修清單們停工 */
    RepairListDetailDispatchRest: (obj: RepairListDetailDispatchRestParams) => Promise<ResultObj>
    /** 維修清單們完成 */
    RepairListDetailDispatchFinish: (obj: RepairListDetailDispatchFinishParams) => Promise<ResultObj>
    /** 維修清單匯出 Excel */
    RepairListDetailReport: (obj: WorkOrderMasterSearchParams) => Promise<ResultObj>;
    /** 新建工單退單折讓單主項 */
    WorkOrderCreditNoteMasterCreate: (obj: WorkOrderCreditNoteMasterCreateParams) => Promise<ResultObj<string>>;
    /** 更新工單折讓單主項 */
    WorkOrderCreditNoteMasterUpdate: (obj: WorkOrderCreditNoteMasterUpdateParams) => Promise<ResultObj>;
    /** 查詢工單折讓單主項  */
    WorkOrderCreditNoteMasterSearch: (obj: WorkOrderCreditNoteMasterSearchParams) => Promise<ResultObj<WorkOrderCreditNoteMasterSearchResItem[]>>;
    /** 新建工單折讓單細項 */
    WorkOrderCreditNoteDetailCreate: (obj: WorkOrderCreditNoteDetailCreateParams) => Promise<ResultObj>;
    /** 工單折讓單細項查詢 */
    WorkOrderCreditNoteDetailSearch: (obj: WorkOrderCreditNoteDetailSearchParams) => Promise<ResultObj<WorkOrderCreditNoteDetailSearchResItem[]>>;
    /** 工單退單結案 */
    WorkOrderCreditNoteMasterComplete: (obj: WorkOrderCreditNoteMasterCompleteParams) => Promise<ResultObj>;
    /** 維修清單出納後更動 (1.僅能更動發票項目及出納ID 2.完工後結案前才能使用) */
    RepairListDetailAfterPayLogUpdate: (obj: RepairListDetailAfterPayLogUpdateParams) => Promise<ResultObj>;
    /** 工單總核賠計算 (回寫至維修清單) */
    WorkOrderMasterTotalInsuranceAndUpdateRLD: (obj: WorkOrderMasterTotalInsuranceAndUpdateRLDParams) => Promise<ResultObj>;
    /** 工單退單折讓項目取消 */
    WorkOrderCreditNoteCancelCreate: (obj: WorkOrderCreditNoteCancelCreateParams) => Promise<ResultObj>;
    /** 工單退單折讓取消項目查詢 */
    WorkOrderCreditNoteCancelSearch: (obj: WorkOrderCreditNoteCancelSearchParams) => Promise<ResultObj<WorkOrderCreditNoteCancelSearchResItem[]>>;
    /*工單庫存領用零件排行查詢*/
    WorkOrderStockRequisitionCommoditySearch: (obj: WorkOrderStockRequisitionCommoditySearchParams) => Promise<ResultObj<WorkOrderStockRequisitionCommoditySearchResItem[]>>;
    /** 新增工單維修狀態Log */
    WorkOrderMaintainStatusLogCreate: (obj: WorkOrderMaintainStatusLogCreateParams) => Promise<ResultObj>;
    /** 修改工單維修狀態Log */
    WorkOrderMaintainStatusLogUpdate: (obj: WorkOrderMaintainStatusLogUpdateParams) => Promise<ResultObj>;
    /** 查詢工單維修狀態Log */
    WorkOrderMaintainStatusLogSearch: (obj: WorkOrderMaintainStatusLogSearchParams) => Promise<ResultObj<WorkOrderMaintainStatusLogSearchResItem[]>>;
    /** 新增工單預計交車時間Log */
    WorkOrderPredictDeliveryLogCreate: (obj: WorkOrderPredictDeliveryLogCreateParams) => Promise<ResultObj>;
    /** 修改工單預計交車時間Log */
    WorkOrderPredictDeliveryLogUpdate: (obj: WorkOrderPredictDeliveryLogUpdateParams) => Promise<ResultObj>;
    /** 查詢工單預計交車時間Log */
    WorkOrderPredictDeliveryLogSearch: (obj: WorkOrderPredictDeliveryLogSearchParams) => Promise<ResultObj<WorkOrderPredictDeliveryLogSearchResItem[]>>;
    /** 工單暫停 */
    WorkOrderMasterTempStop: (obj: WorkOrderMasterTempStopParams) => Promise<ResultObj>;
    /** 工單取消暫停 */
    WorkOrderMasterTempStopCancel: (obj: WorkOrderMasterTempStopCancelParams) => Promise<ResultObj>;

}

// WorkOrderApiManager 類別實現
export class WorkOrderApiManager implements WorkOrderApiInterface {

    private adapter: WorkOrderDotnet6Aadpter | WorkOrderFrameworkAadpter | null = null;
    private version: keyof typeof API_Version | undefined;

    constructor(version?: keyof typeof API_Version) {
        this.version = version;
    }

    async init(): Promise<WorkOrderApiManager> {
        let target_version: string | undefined = this.version;
        if (target_version === undefined) {
            let getAPIversionResult: ApiVersionInfo | null = await getControllerApiVersion("WorkOrder");
            target_version = getAPIversionResult === null ? this.version : getAPIversionResult.version;
        }

        // 若仍無法取得版本（通常是因為尚未登入，company_code 為空），
        // 丟出錯誤讓 useWorkOrderApi 的 catch 攔截，避免註冊帶有 null adapter 的管理員
        // 以防止下次登入後沿用此無效的管理員實例
        if (target_version === undefined) {
            throw new Error("WorkOrderApiManager: 無法初始化，尚未取得 API 版本（可能尚未登入）");
        }

        if (target_version === "dotNet6") this.adapter = new WorkOrderDotnet6Aadpter();
        else if (target_version === 'dotNet6_AM_N') this.adapter = new WorkOrderDotnet6Aadpter();//預設是 dotNet6，有需要才再另寫Adapter
        else if (target_version === "framework") this.adapter = new WorkOrderFrameworkAadpter();

        if (this.adapter !== null) {
            this.WorkOrderMasterCreate = this.adapter.WorkOrderMasterCreate;
            this.WorkOrderMasterSearch = this.adapter.WorkOrderMasterSearch;
            this.WorkOrderMasterDetail = this.adapter.WorkOrderMasterDetail;
            this.WorkOrderMasterCompleteCancel = this.adapter.WorkOrderMasterCompleteCancel;
            this.WorkOrderMasterCaseCloseCancel = this.adapter.WorkOrderMasterCaseCloseCancel;
            this.WorkOrderMasterUpdate = this.adapter.WorkOrderMasterUpdate;
            this.WorkOrderTurnoverRepairDetailCreate = this.adapter.WorkOrderTurnoverRepairDetailCreate;
            this.WorkOrderTurnoverRepairDetailSearch = this.adapter.WorkOrderTurnoverRepairDetailSearch;
            this.WorkOrderTurnoverRepairDetailUpdate = this.adapter.WorkOrderTurnoverRepairDetailUpdate;
            this.WorkOrderTurnoverRepairDetailDelete = this.adapter.WorkOrderTurnoverRepairDetailDelete;
            this.WorkOrderMasterInvalid = this.adapter.WorkOrderMasterInvalid;
            this.WorkOrderPayLogSearch = this.adapter.WorkOrderPayLogSearch;
            this.WorkOrderPayLogCreate = this.adapter.WorkOrderPayLogCreate;
            this.WorkOrderPayLogUpdate = this.adapter.WorkOrderPayLogUpdate;
            this.WorkOrderPayLogDelete = this.adapter.WorkOrderPayLogDelete;
            this.WorkOrderPayLogDetailCreate = this.adapter.WorkOrderPayLogDetailCreate;
            this.WorkOrderPayLogDetailDelete = this.adapter.WorkOrderPayLogDetailDelete;
            this.WorkOrderPayLogDetailSearch = this.adapter.WorkOrderPayLogDetailSearch;
            this.WorkOrderExtFinish = this.adapter.WorkOrderExtFinish;
            this.WorkOrderExtFinishCancel = this.adapter.WorkOrderExtFinishCancel;
            this.WorkOrderMasterComplete = this.adapter.WorkOrderMasterComplete;
            this.WorkOrderMasterCaseClose = this.adapter.WorkOrderMasterCaseClose;
            this.WorkOrderStockRequisitionCreate = this.adapter.WorkOrderStockRequisitionCreate;
            this.WorkOrderStockRequisitionSearch = this.adapter.WorkOrderStockRequisitionSearch;
            this.WorkOrderStockRequisitionUpdate = this.adapter.WorkOrderStockRequisitionUpdate;
            this.WorkOrderStockRequisitionRecordPrintTime = this.adapter.WorkOrderStockRequisitionRecordPrintTime;
            this.WorkOrderStockRequisitionInvalid = this.adapter.WorkOrderStockRequisitionInvalid;
            this.WorkOrderStockRequisitionDelete = this.adapter.WorkOrderStockRequisitionDelete;
            this.RepairListDetailCreate = this.adapter.RepairListDetailCreate;
            this.RepairListDetailSearch = this.adapter.RepairListDetailSearch;
            this.RepairListDetailCheck = this.adapter.RepairListDetailCheck;
            this.RepairListDetailUpdate = this.adapter.RepairListDetailUpdate;
            this.RepairListDetailDelete = this.adapter.RepairListDetailDelete;
            this.RepairListDetailDispatch = this.adapter.RepairListDetailDispatch;
            this.RepairListDetailDispatchConstruct = this.adapter.RepairListDetailDispatchConstruct;
            this.RepairListDetailDispatchRest = this.adapter.RepairListDetailDispatchRest;
            this.RepairListDetailDispatchFinish = this.adapter.RepairListDetailDispatchFinish;
            this.RepairListDetailReport = this.adapter.RepairListDetailReport;
            this.WorkOrderCreditNoteMasterCreate = this.adapter.WorkOrderCreditNoteMasterCreate;
            this.WorkOrderCreditNoteMasterUpdate = this.adapter.WorkOrderCreditNoteMasterUpdate;
            this.WorkOrderCreditNoteMasterSearch = this.adapter.WorkOrderCreditNoteMasterSearch;
            this.WorkOrderCreditNoteDetailCreate = this.adapter.WorkOrderCreditNoteDetailCreate;
            this.WorkOrderCreditNoteDetailSearch = this.adapter.WorkOrderCreditNoteDetailSearch;
            this.WorkOrderCreditNoteMasterComplete = this.adapter.WorkOrderCreditNoteMasterComplete;
            this.RepairListDetailAfterPayLogUpdate = this.adapter.RepairListDetailAfterPayLogUpdate;
            this.WorkOrderMasterTotalInsuranceAndUpdateRLD = this.adapter.WorkOrderMasterTotalInsuranceAndUpdateRLD;
            this.WorkOrderCreditNoteCancelCreate = this.adapter.WorkOrderCreditNoteCancelCreate;
            this.WorkOrderCreditNoteCancelSearch = this.adapter.WorkOrderCreditNoteCancelSearch;
            this.WorkOrderStockRequisitionCommoditySearch = this.adapter.WorkOrderStockRequisitionCommoditySearch;
            this.WorkOrderMaintainStatusLogCreate = this.adapter.WorkOrderMaintainStatusLogCreate;
            this.WorkOrderMaintainStatusLogUpdate = this.adapter.WorkOrderMaintainStatusLogUpdate;
            this.WorkOrderMaintainStatusLogSearch = this.adapter.WorkOrderMaintainStatusLogSearch;
            this.WorkOrderPredictDeliveryLogCreate = this.adapter.WorkOrderPredictDeliveryLogCreate;
            this.WorkOrderPredictDeliveryLogUpdate = this.adapter.WorkOrderPredictDeliveryLogUpdate;
            this.WorkOrderPredictDeliveryLogSearch = this.adapter.WorkOrderPredictDeliveryLogSearch;
            this.WorkOrderMasterTempStop = this.adapter.WorkOrderMasterTempStop;
            this.WorkOrderMasterTempStopCancel = this.adapter.WorkOrderMasterTempStopCancel;
        }
        return this;
    }

    emptyAdapter = async (): Promise<ResultObj> => ({ success: false, message: 'API未實現[WorkOrderAdapter]', data: null });

    WorkOrderMasterCreate = async (obj: WorkOrderMasterCreateParams): Promise<ResultObj> => await this.emptyAdapter();
    WorkOrderMasterSearch = async (obj: WorkOrderMasterSearchParams): Promise<ResultObj<WorkOrderMasterSearchResItem[]>> => await this.emptyAdapter();
    WorkOrderMasterDetail = async (obj: WorkOrderMasterDetailParams): Promise<ResultObj<WorkOrderMasterDetailResItem>> => await this.emptyAdapter();
    WorkOrderMasterCompleteCancel = async (obj: WorkOrderMasterCompleteCancelParams): Promise<ResultObj> => await this.emptyAdapter();
    WorkOrderMasterCaseCloseCancel = async (obj: WorkOrderMasterCaseCloseCancelParams): Promise<ResultObj> => await this.emptyAdapter();
    WorkOrderMasterUpdate = async (obj: WorkOrderMasterUpdateParams): Promise<ResultObj> => await this.emptyAdapter();
    WorkOrderTurnoverRepairDetailCreate = async (obj: WorkOrderTurnoverRepairDetailCreateParams): Promise<ResultObj> => await this.emptyAdapter();
    WorkOrderTurnoverRepairDetailSearch = async (obj: WorkOrderTurnoverRepairDetailSearchParams): Promise<ResultObj> => await this.emptyAdapter();
    WorkOrderTurnoverRepairDetailUpdate = async (obj: WorkOrderTurnoverRepairDetailUpdateParams): Promise<ResultObj> => await this.emptyAdapter();
    WorkOrderTurnoverRepairDetailDelete = async (obj: WorkOrderTurnoverRepairDetailDeleteParams): Promise<ResultObj> => await this.emptyAdapter();
    WorkOrderMasterInvalid = async (obj: WorkOrderMasterInvalidParams): Promise<ResultObj> => await this.emptyAdapter();
    WorkOrderPayLogSearch = async (obj: WorkOrderPayLogSearchParams): Promise<ResultObj<WorkOrderPayLogSearchResItem[]>> => await this.emptyAdapter();
    WorkOrderPayLogCreate = async (obj: WorkOrderPayLogCreateParams): Promise<ResultObj> => await this.emptyAdapter();
    WorkOrderPayLogUpdate = async (obj: WorkOrderPayLogUpdateParams): Promise<ResultObj> => await this.emptyAdapter();
    WorkOrderPayLogDelete = async (obj: WorkOrderPayLogDeleteParams): Promise<ResultObj> => await this.emptyAdapter();
    WorkOrderPayLogDetailCreate = async (obj: WorkOrderPayLogDetailCreateParams): Promise<ResultObj> => await this.emptyAdapter();
    WorkOrderPayLogDetailDelete = async (obj: WorkOrderPayLogDetailDeleteParams): Promise<ResultObj> => await this.emptyAdapter();
    WorkOrderPayLogDetailSearch = async (obj: WorkOrderPayLogDetailSearchParams): Promise<ResultObj<WorkOrderPayLogDetailSearchResItem[]>> => await this.emptyAdapter();
    WorkOrderExtFinish = async (obj: WorkOrderExtFinishParams): Promise<ResultObj> => await this.emptyAdapter();
    WorkOrderExtFinishCancel = async (obj: WorkOrderExtFinishCancelParams): Promise<ResultObj> => await this.emptyAdapter();
    WorkOrderMasterComplete = async (obj: WorkOrderMasterCompleteParams): Promise<ResultObj> => await this.emptyAdapter();
    WorkOrderMasterCaseClose = async (obj: WorkOrderMasterCaseCloseParams): Promise<ResultObj> => await this.emptyAdapter();
    WorkOrderStockRequisitionCreate = async (obj: WorkOrderStockRequisitionCreateParams): Promise<ResultObj> => await this.emptyAdapter();
    WorkOrderStockRequisitionSearch = async (obj: WorkOrderStockRequisitionSearchParams): Promise<ResultObj> => await this.emptyAdapter();
    WorkOrderStockRequisitionUpdate = async (obj: WorkOrderStockRequisitionUpdateParams): Promise<ResultObj> => await this.emptyAdapter();
    WorkOrderStockRequisitionRecordPrintTime = async (obj: WorkOrderStockRequisitionRecordPrintTimeParams): Promise<ResultObj> => await this.emptyAdapter();
    WorkOrderStockRequisitionInvalid = async (obj: WorkOrderStockRequisitionInvalidParams): Promise<ResultObj> => await this.emptyAdapter();
    WorkOrderStockRequisitionDelete = async (obj: WorkOrderStockRequisitionDeleteParams): Promise<ResultObj> => await this.emptyAdapter();
    RepairListDetailCreate = async (obj: RepairListDetailCreateParams): Promise<ResultObj> => await this.emptyAdapter();
    RepairListDetailSearch = async (obj: RepairListDetailSearchParams): Promise<ResultObj<RepairListDetailSearchResItem[]>> => await this.emptyAdapter();
    RepairListDetailCheck = async (obj: RepairListDetailCheckParams): Promise<ResultObj<RepairListDetailCheckResItem>> => await this.emptyAdapter();
    RepairListDetailUpdate = async (obj: RepairListDetailUpdateParams): Promise<ResultObj> => await this.emptyAdapter();
    RepairListDetailDelete = async (obj: RepairListDetailDeleteParams): Promise<ResultObj> => await this.emptyAdapter();
    RepairListDetailDispatch = async (obj: RepairListDetailDispatchParams): Promise<ResultObj> => await this.emptyAdapter();
    RepairListDetailDispatchConstruct = async (obj: RepairListDetailDispatchConstructParams): Promise<ResultObj> => await this.emptyAdapter();
    RepairListDetailDispatchRest = async (obj: RepairListDetailDispatchRestParams): Promise<ResultObj> => await this.emptyAdapter();
    RepairListDetailDispatchFinish = async (obj: RepairListDetailDispatchFinishParams): Promise<ResultObj> => await this.emptyAdapter();
    RepairListDetailReport = async (obj: WorkOrderMasterSearchParams): Promise<ResultObj> => await this.emptyAdapter();
    WorkOrderCreditNoteMasterCreate = async (obj: WorkOrderCreditNoteMasterCreateParams): Promise<ResultObj<string>> => await this.emptyAdapter();
    WorkOrderCreditNoteMasterUpdate = async (obj: WorkOrderCreditNoteMasterUpdateParams): Promise<ResultObj> => await this.emptyAdapter();
    WorkOrderCreditNoteMasterSearch = async (obj: WorkOrderCreditNoteMasterSearchParams): Promise<ResultObj<WorkOrderCreditNoteMasterSearchResItem[]>> => await this.emptyAdapter();
    WorkOrderCreditNoteDetailCreate = async (obj: WorkOrderCreditNoteDetailCreateParams): Promise<ResultObj> => await this.emptyAdapter();
    WorkOrderCreditNoteDetailSearch = async (obj: WorkOrderCreditNoteDetailSearchParams): Promise<ResultObj<WorkOrderCreditNoteDetailSearchResItem[]>> => await this.emptyAdapter();
    WorkOrderCreditNoteMasterComplete = async (obj: WorkOrderCreditNoteMasterCompleteParams): Promise<ResultObj> => await this.emptyAdapter();
    RepairListDetailAfterPayLogUpdate = async (obj: RepairListDetailAfterPayLogUpdateParams): Promise<ResultObj> => await this.emptyAdapter();
    WorkOrderMasterTotalInsuranceAndUpdateRLD = async (obj: WorkOrderMasterTotalInsuranceAndUpdateRLDParams): Promise<ResultObj> => await this.emptyAdapter();
    WorkOrderCreditNoteCancelCreate = async (obj: WorkOrderCreditNoteCancelCreateParams): Promise<ResultObj> => await this.emptyAdapter();
    WorkOrderCreditNoteCancelSearch = async (obj: WorkOrderCreditNoteCancelSearchParams): Promise<ResultObj> => await this.emptyAdapter();
    WorkOrderStockRequisitionCommoditySearch = async (obj: WorkOrderStockRequisitionCommoditySearchParams): Promise<ResultObj<WorkOrderStockRequisitionCommoditySearchResItem[]>> => await this.emptyAdapter();
    WorkOrderMaintainStatusLogCreate = async (obj: WorkOrderMaintainStatusLogCreateParams): Promise<ResultObj> => await this.emptyAdapter();
    WorkOrderMaintainStatusLogUpdate = async (obj: WorkOrderMaintainStatusLogUpdateParams): Promise<ResultObj> => await this.emptyAdapter();
    WorkOrderMaintainStatusLogSearch = async (obj: WorkOrderMaintainStatusLogSearchParams): Promise<ResultObj<WorkOrderMaintainStatusLogSearchResItem[]>> => await this.emptyAdapter();
    WorkOrderPredictDeliveryLogCreate = async (obj: WorkOrderPredictDeliveryLogCreateParams): Promise<ResultObj> => await this.emptyAdapter();
    WorkOrderPredictDeliveryLogUpdate = async (obj: WorkOrderPredictDeliveryLogUpdateParams): Promise<ResultObj> => await this.emptyAdapter();
    WorkOrderPredictDeliveryLogSearch = async (obj: WorkOrderPredictDeliveryLogSearchParams): Promise<ResultObj<WorkOrderPredictDeliveryLogSearchResItem[]>> => await this.emptyAdapter();
    WorkOrderMasterTempStop = async (obj: WorkOrderMasterTempStopParams): Promise<ResultObj> => await this.emptyAdapter();
    WorkOrderMasterTempStopCancel = async (obj: WorkOrderMasterTempStopCancelParams): Promise<ResultObj> => await this.emptyAdapter();
}

class WorkOrderDotnet6Aadpter implements WorkOrderApiInterface {
    WorkOrderMasterCreate = async (obj: WorkOrderMasterCreateParams): Promise<ResultObj> => await allFetch('WorkOrder/WorkOrderMasterCreate', {
        ...obj,
        predict_delivery_datetime: defaultDateTimeToDotNet6Format(obj.predict_delivery_datetime),
        time_enter: defaultDateTimeToDotNet6Format(obj.time_enter),
    });
    WorkOrderMasterSearch = async (obj: WorkOrderMasterSearchParams): Promise<ResultObj<WorkOrderMasterSearchResItem[]>> => await allFetch(
        'WorkOrder/WorkOrderMasterSearch',
        {
            ...obj,
            time_enter_start: defaultDateTimeToDotNet6Format(obj.time_enter_start),
            time_enter_end: defaultDateTimeToDotNet6Format(obj.time_enter_end),
            time_leave_start: defaultDateTimeToDotNet6Format(obj.time_leave_start),
            time_leave_end: defaultDateTimeToDotNet6Format(obj.time_leave_end),
            invalid_time_start: defaultDateTimeToDotNet6Format(obj.invalid_time_start),
            invalid_time_end: defaultDateTimeToDotNet6Format(obj.invalid_time_end),
            complete_time_start: defaultDateTimeToDotNet6Format(obj.complete_time_start),
            complete_time_end: defaultDateTimeToDotNet6Format(obj.complete_time_end),
            caseclose_time_start: defaultDateTimeToDotNet6Format(obj.caseclose_time_start),
            caseclose_time_end: defaultDateTimeToDotNet6Format(obj.caseclose_time_end),
            predict_delivery_datetime_start: defaultDateTimeToDotNet6Format(obj.predict_delivery_datetime_start),
            predict_delivery_datetime_end: defaultDateTimeToDotNet6Format(obj.predict_delivery_datetime_end),
        }
    ).then((res: ResultObj<WorkOrderMasterSearchResItem[]>) => {
        if (obj.excel === 'Y') {
            return ({
                ...res,
                exportPath: ExportURL + "/" + res.message,
                data: res.data && res.data.length > 0 ?
                    res.data.map(item => ({
                        ...item,
                        time_enter: dotNet6DateTimeToDefaulFormat(item.time_enter),
                        predict_delivery_datetime: dotNet6DateTimeToDefaulFormat(item.predict_delivery_datetime),
                    })) :
                    res.data
            })
        } else {
            return ({
                ...res,
                data: res.data && res.data.length > 0 ?
                    res.data.map(item => ({
                        ...item,
                        time_enter: dotNet6DateTimeToDefaulFormat(item.time_enter),
                        predict_delivery_datetime: dotNet6DateTimeToDefaulFormat(item.predict_delivery_datetime),
                    })) :
                    res.data
            })
        }
    })
    WorkOrderMasterDetail = async (obj: WorkOrderMasterDetailParams): Promise<ResultObj<WorkOrderMasterDetailResItem>> => await allFetch('WorkOrder/WorkOrderMasterDetail', obj).then((res: ResultObj<WorkOrderMasterDetailResItem[]> | any) => {
        const data = Object.getPrototypeOf(res.data).constructor.name === "Array"
            ? (res.data.length > 0 ? res.data[0] : null)
            : res.data;
        return {
            ...res,
            data: data ? {
                ...data,
                time_enter: dotNet6DateTimeToDefaulFormat(data.time_enter),
                time_leave: dotNet6DateTimeToDefaulFormat(data.time_leave),
                // complete_time/invalid_time 後端未填值時可能回傳 null，需轉成空字串，否則畫面的 allDisabled 判斷會誤判為已完工/已作廢
                complete_time: dotNet6DateTimeToDefaulFormat(data.complete_time),
                invalid_time: dotNet6DateTimeToDefaulFormat(data.invalid_time),
            } : data
        }
    });
    WorkOrderMasterCompleteCancel = async (obj: WorkOrderMasterCompleteCancelParams): Promise<ResultObj> => await allFetch('WorkOrder/WorkOrderMasterCompleteCancel', obj);
    WorkOrderMasterCaseCloseCancel = async (obj: WorkOrderMasterCaseCloseCancelParams): Promise<ResultObj> => await allFetch('WorkOrder/WorkOrderMasterCaseCloseCancel', obj);
    WorkOrderMasterUpdate = async (obj: WorkOrderMasterUpdateParams): Promise<ResultObj> => await allFetch('WorkOrder/WorkOrderMasterUpdate', {
        ...obj,
        time_enter: defaultDateTimeToDotNet6Format(obj.time_enter),
        predict_delivery_datetime: defaultDateTimeToDotNet6Format(obj.predict_delivery_datetime),
    });
    WorkOrderTurnoverRepairDetailCreate = async (obj: WorkOrderTurnoverRepairDetailCreateParams): Promise<ResultObj> => await allFetch('WorkOrder/WorkOrderTurnoverRepairDetailCreate', obj);
    WorkOrderTurnoverRepairDetailSearch = async (obj: WorkOrderTurnoverRepairDetailSearchParams): Promise<ResultObj<WorkOrderTurnoverRepairDetailSearchPesItem[]>> => await allFetch('WorkOrder/WorkOrderTurnoverRepairDetailSearch', obj);
    WorkOrderTurnoverRepairDetailUpdate = async (obj: WorkOrderTurnoverRepairDetailUpdateParams): Promise<ResultObj> => await allFetch('WorkOrder/WorkOrderTurnoverRepairDetailUpdate', obj);
    WorkOrderTurnoverRepairDetailDelete = async (obj: WorkOrderTurnoverRepairDetailDeleteParams): Promise<ResultObj> => await allFetch('WorkOrder/WorkOrderTurnoverRepairDetailDelete', obj);
    WorkOrderMasterInvalid = async (obj: WorkOrderMasterInvalidParams): Promise<ResultObj> => await allFetch('WorkOrder/WorkOrderMasterInvalid', obj);
    WorkOrderPayLogSearch = async (obj: WorkOrderPayLogSearchParams): Promise<ResultObj<WorkOrderPayLogSearchResItem[]>> => await allFetch('WorkOrder/WorkOrderPayLogSearch', obj)
        .then((res: ResultObj<WorkOrderPayLogSearchResItem[]>) => ({
            ...res,
            data: res.data && Object.getPrototypeOf(res.data).constructor.name === "Array"
                ? res.data.map((item: WorkOrderPayLogSearchResItem) => ({
                    ...item,
                    pay_date: dotNet6DateToDefaulFormat(item.pay_date),
                    invoice_date: dotNet6DateToDefaulFormat(item.invoice_date),
                }))
                : res.data,
        }))
    WorkOrderPayLogCreate = async (obj: WorkOrderPayLogCreateParams): Promise<ResultObj> => await allFetch('WorkOrder/WorkOrderPayLogCreate', {
        ...obj,
        pay_date: defaultDateTimeToDotNet6Format(obj.pay_date),
        invoice_date: defaultDateTimeToDotNet6Format(obj.invoice_date),
    });
    WorkOrderPayLogUpdate = async (obj: WorkOrderPayLogUpdateParams): Promise<ResultObj> => await allFetch('WorkOrder/WorkOrderPayLogUpdate', {
        ...obj,
        pay_date: defaultDateTimeToDotNet6Format(obj.pay_date),
        invoice_date: defaultDateTimeToDotNet6Format(obj.invoice_date),
    });
    WorkOrderPayLogDelete = async (obj: WorkOrderPayLogDeleteParams): Promise<ResultObj> => await allFetch('WorkOrder/WorkOrderPayLogDelete', obj);
    WorkOrderPayLogDetailCreate = async (obj: WorkOrderPayLogDetailCreateParams): Promise<ResultObj> => await allFetch('WorkOrder/WorkOrderPayLogDetailCreate', obj);
    WorkOrderPayLogDetailDelete = async (obj: WorkOrderPayLogDetailDeleteParams): Promise<ResultObj> => await allFetch('WorkOrder/WorkOrderPayLogDetailDelete', obj);
    WorkOrderPayLogDetailSearch = async (obj: WorkOrderPayLogDetailSearchParams): Promise<ResultObj<WorkOrderPayLogDetailSearchResItem[]>> => await allFetch('WorkOrder/WorkOrderPayLogDetailSearch', obj);
    WorkOrderExtFinish = async (obj: WorkOrderExtFinishParams) => await allFetch('WorkOrder/WorkOrderExtFinish', obj);
    WorkOrderExtFinishCancel = async (obj: WorkOrderExtFinishCancelParams) => await allFetch('WorkOrder/WorkOrderExtFinishCancel', obj);
    WorkOrderMasterComplete = async (obj: WorkOrderMasterCompleteParams): Promise<ResultObj> => await allFetch('WorkOrder/WorkOrderMasterComplete', obj);
    WorkOrderMasterCaseClose = async (obj: WorkOrderMasterCaseCloseParams): Promise<ResultObj> => await allFetch('WorkOrder/WorkOrderMasterCaseClose', obj);
    WorkOrderStockRequisitionCreate = async (obj: WorkOrderStockRequisitionCreateParams): Promise<ResultObj> => await allFetch('WorkOrder/WorkOrderStockRequisitionCreate', obj);
    WorkOrderStockRequisitionSearch = async (obj: WorkOrderStockRequisitionSearchParams): Promise<ResultObj<WorkOrderStockRequisitionSearchResItem[]>> => await allFetch('WorkOrder/WorkOrderStockRequisitionSearch', obj);
    WorkOrderStockRequisitionUpdate = async (obj: WorkOrderStockRequisitionUpdateParams): Promise<ResultObj> => await allFetch('WorkOrder/WorkOrderStockRequisitionUpdate', obj);
    WorkOrderStockRequisitionRecordPrintTime = async (obj: WorkOrderStockRequisitionRecordPrintTimeParams): Promise<ResultObj> => await allFetch('WorkOrder/WorkOrderStockRequisitionRecordPrintTime', obj);
    WorkOrderStockRequisitionInvalid = async (obj: WorkOrderStockRequisitionInvalidParams): Promise<ResultObj> => await allFetch('WorkOrder/WorkOrderStockRequisitionInvalid', obj);
    WorkOrderStockRequisitionDelete = async (obj: WorkOrderStockRequisitionDeleteParams): Promise<ResultObj> => await allFetch('WorkOrder/WorkOrderStockRequisitionDelete', obj);
    RepairListDetailCreate = async (obj: RepairListDetailCreateParams): Promise<ResultObj> => await allFetch('WorkOrder/RepairListDetailCreate', obj);
    RepairListDetailSearch = async (obj: RepairListDetailSearchParams): Promise<ResultObj<RepairListDetailSearchResItem[]>> => await allFetch('WorkOrder/RepairListDetailSearch', obj);
    RepairListDetailCheck = async (obj: RepairListDetailCheckParams): Promise<ResultObj<RepairListDetailCheckResItem>> => await allFetch('WorkOrder/RepairListDetailCheck', obj);
    RepairListDetailUpdate = async (obj: RepairListDetailUpdateParams): Promise<ResultObj> => await allFetch('WorkOrder/RepairListDetailUpdate', obj);
    RepairListDetailDelete = async (obj: RepairListDetailDeleteParams): Promise<ResultObj> => await allFetch('WorkOrder/RepairListDetailDelete', obj);
    RepairListDetailDispatch = async (obj: RepairListDetailDispatchParams): Promise<ResultObj> => await allFetch('WorkOrder/RepairListDetailDispatch', obj);
    RepairListDetailDispatchConstruct = async (obj: RepairListDetailDispatchConstructParams): Promise<ResultObj> => await allFetch('WorkOrder/RepairListDetailDispatchConstruct', obj);
    RepairListDetailDispatchRest = async (obj: RepairListDetailDispatchRestParams): Promise<ResultObj> => await allFetch('WorkOrder/RepairListDetailDispatchRest', obj);
    RepairListDetailDispatchFinish = async (obj: RepairListDetailDispatchFinishParams): Promise<ResultObj> => await allFetch('WorkOrder/RepairListDetailDispatchFinish', obj);
    RepairListDetailReport = async (obj: WorkOrderMasterSearchParams): Promise<ResultObj> => await allFetch('Report/RepairListDetailReport', {
        ...obj,
        time_enter_start: defaultDateTimeToDotNet6Format(obj.time_enter_start),
        time_enter_end: defaultDateTimeToDotNet6Format(obj.time_enter_end),
        time_leave_start: defaultDateTimeToDotNet6Format(obj.time_leave_start),
        time_leave_end: defaultDateTimeToDotNet6Format(obj.time_leave_end),
        invalid_time_start: defaultDateTimeToDotNet6Format(obj.invalid_time_start),
        invalid_time_end: defaultDateTimeToDotNet6Format(obj.invalid_time_end),
        complete_time_start: defaultDateTimeToDotNet6Format(obj.complete_time_start),
        complete_time_end: defaultDateTimeToDotNet6Format(obj.complete_time_end),
        caseclose_time_start: defaultDateTimeToDotNet6Format(obj.caseclose_time_start),
        caseclose_time_end: defaultDateTimeToDotNet6Format(obj.caseclose_time_end),
        predict_delivery_datetime_start: defaultDateTimeToDotNet6Format(obj.predict_delivery_datetime_start),
        predict_delivery_datetime_end: defaultDateTimeToDotNet6Format(obj.predict_delivery_datetime_end),
    });
    WorkOrderCreditNoteMasterCreate = async (obj: WorkOrderCreditNoteMasterCreateParams): Promise<ResultObj<string>> => await allFetch("WorkOrder/WorkOrderCreditNoteMasterCreate", {
        ...obj,
        date: defaultDateTimeToDotNet6Format(obj.date),
    });
    WorkOrderCreditNoteMasterUpdate = async (obj: WorkOrderCreditNoteMasterUpdateParams): Promise<ResultObj> => await allFetch("WorkOrder/WorkOrderCreditNoteMasterUpdate", {
        ...obj,
        date: defaultDateTimeToDotNet6Format(obj.date),
    });
    WorkOrderCreditNoteMasterSearch = async (obj: WorkOrderCreditNoteMasterSearchParams): Promise<ResultObj<WorkOrderCreditNoteMasterSearchResItem[]>> => await allFetch("WorkOrder/WorkOrderCreditNoteMasterSearch", {
        ...obj,
        date: defaultDateTimeToDotNet6Format(obj.date),
        complete_time_start: defaultDateTimeToDotNet6Format(obj.complete_time_start),
        complete_time_end: defaultDateTimeToDotNet6Format(obj.complete_time_end),
    })
        .then((res: ResultObj) => {
            return {
                ...res,
                data: res.data && Object.getPrototypeOf(res.data).constructor.name === "Array" && res.data.length > 0
                    ? res.data.map((item: WorkOrderCreditNoteMasterSearchResItem) => ({
                        ...item,
                        complete_time: dotNet6DateTimeToDefaulFormat(item.complete_time),
                        cre_time: dotNet6DateTimeToDefaulFormat(item.cre_time),
                    }))
                    : res.data,
            }
        })
    WorkOrderCreditNoteDetailCreate = async (obj: WorkOrderCreditNoteDetailCreateParams): Promise<ResultObj> => await allFetch("WorkOrder/WorkOrderCreditNoteDetailCreate", obj);
    WorkOrderCreditNoteDetailSearch = async (obj: WorkOrderCreditNoteDetailSearchParams): Promise<ResultObj<WorkOrderCreditNoteDetailSearchResItem[]>> => await allFetch("WorkOrder/WorkOrderCreditNoteDetailSearch", obj);
    WorkOrderCreditNoteMasterComplete = async (obj: WorkOrderCreditNoteMasterCompleteParams): Promise<ResultObj> => await allFetch("WorkOrder/WorkOrderCreditNoteMasterComplete", obj);
    RepairListDetailAfterPayLogUpdate = async (obj: RepairListDetailAfterPayLogUpdateParams): Promise<ResultObj> => await allFetch("WorkOrder/RepairListDetailAfterPayLogUpdate", obj);
    WorkOrderMasterTotalInsuranceAndUpdateRLD = async (obj: WorkOrderMasterTotalInsuranceAndUpdateRLDParams): Promise<ResultObj> => await allFetch("WorkOrder/WorkOrderMasterTotalInsuranceAndUpdateRLD", obj);
    WorkOrderCreditNoteCancelCreate = async (obj: WorkOrderCreditNoteCancelCreateParams): Promise<ResultObj> => await allFetch("WorkOrder/WorkOrderCreditNoteCancelCreate", obj);
    WorkOrderCreditNoteCancelSearch = async (obj: WorkOrderCreditNoteCancelSearchParams): Promise<ResultObj<WorkOrderCreditNoteCancelSearchResItem[]>> => await allFetch("WorkOrder/WorkOrderCreditNoteCancelSearch", obj);
    WorkOrderStockRequisitionCommoditySearch = async (obj: WorkOrderStockRequisitionCommoditySearchParams): Promise<ResultObj<WorkOrderStockRequisitionCommoditySearchResItem[]>> => await allFetch("WorkOrder/WorkOrderStockRequisitionCommoditySearch", {
        ...obj,
        cre_time_start: defaultRangeDateToDotNet6Format(obj.cre_time_start, "start"),
        cre_time_end: defaultRangeDateToDotNet6Format(obj.cre_time_end, "end"),
    });
    WorkOrderMaintainStatusLogCreate = async (obj: WorkOrderMaintainStatusLogCreateParams): Promise<ResultObj> => await allFetch("WorkOrder/WorkOrderMaintainStatusLogCreate", obj);
    WorkOrderMaintainStatusLogUpdate = async (obj: WorkOrderMaintainStatusLogUpdateParams): Promise<ResultObj> => await allFetch("WorkOrder/WorkOrderMaintainStatusLogUpdate", obj);
    WorkOrderMaintainStatusLogSearch = async (obj: WorkOrderMaintainStatusLogSearchParams): Promise<ResultObj<WorkOrderMaintainStatusLogSearchResItem[]>> => await allFetch("WorkOrder/WorkOrderMaintainStatusLogSearch", obj);
    WorkOrderPredictDeliveryLogCreate = async (obj: WorkOrderPredictDeliveryLogCreateParams): Promise<ResultObj> => await allFetch("WorkOrder/WorkOrderPredictDeliveryLogCreate", {
        ...obj,
        predict_delivery_datetime: defaultDateTimeToDotNet6Format(obj.predict_delivery_datetime),
    });
    WorkOrderPredictDeliveryLogUpdate = async (obj: WorkOrderPredictDeliveryLogUpdateParams): Promise<ResultObj> => await allFetch("WorkOrder/WorkOrderPredictDeliveryLogUpdate", {
        ...obj,
        predict_delivery_datetime: defaultDateTimeToDotNet6Format(obj.predict_delivery_datetime),
    });
    WorkOrderPredictDeliveryLogSearch = async (obj: WorkOrderPredictDeliveryLogSearchParams): Promise<ResultObj<WorkOrderPredictDeliveryLogSearchResItem[]>> => await allFetch("WorkOrder/WorkOrderPredictDeliveryLogSearch", {
        ...obj,
        predict_delivery_datetime: defaultDateTimeToDotNet6Format(obj.predict_delivery_datetime),
    }).then((res: ResultObj) => {
        return {
            ...res,
            data: res.data && Object.getPrototypeOf(res.data).constructor.name === "Array" && res.data.length > 0
                ? res.data.map((item: WorkOrderPredictDeliveryLogSearchResItem) => ({
                    ...item,
                    predict_delivery_datetime: dotNet6DateTimeToDefaulFormat(item.predict_delivery_datetime),
                }))
                : res.data,
        }
    });
    WorkOrderMasterTempStop = async (obj: WorkOrderMasterTempStopParams): Promise<ResultObj> => await allFetch('WorkOrder/WorkOrderMasterTempStop', obj);
    WorkOrderMasterTempStopCancel = async (obj: WorkOrderMasterTempStopCancelParams): Promise<ResultObj> => await allFetch('WorkOrder/WorkOrderMasterTempStopCancel', obj);
}

class WorkOrderFrameworkAadpter implements WorkOrderApiInterface {
    emptyAdapter = async (): Promise<ResultObj> => ({ success: false, message: 'API未實現[WorkOrderAdapter]', data: null });

    WorkOrderMasterCreate = async (obj: WorkOrderMasterCreateParams): Promise<ResultObj> => await allFetch('WorkOrder/WorkOrderMasterCreate', obj);
    WorkOrderMasterSearch = async (obj: WorkOrderMasterSearchParams): Promise<ResultObj<WorkOrderMasterSearchResItem[]>> =>
        await allFetch('WorkOrder/WorkOrderMasterSearch', obj)
            .then((res: ResultObj<WorkOrderMasterSearchResItem[]>) => {
                if (obj.excel === 'Y') {
                    return ({
                        ...res,
                        exportPath: ExportURL + "/" + res.data
                    })
                } else {
                    return ({
                        ...res,
                    })
                }
            })
    WorkOrderMasterDetail = async (obj: WorkOrderMasterDetailParams): Promise<ResultObj<WorkOrderMasterDetailResItem>> =>
        await allFetch('WorkOrder/WorkOrderMasterDetail', obj)
            .then((res: ResultObj<WorkOrderMasterDetailResItem[] | any>) => ({
                success: res.success,
                message: res.message,
                page: res.page,
                data: Object.getPrototypeOf(res.data).constructor.name === "Array" && res.data.length > 0
                    ? res.data[0]
                    : null
            }))
    WorkOrderMasterCompleteCancel = async (obj: WorkOrderMasterCompleteCancelParams): Promise<ResultObj> => await allFetch('WorkOrder/WorkOrderMasterCompleteCancel', obj);
    WorkOrderMasterCaseCloseCancel = async (obj: WorkOrderMasterCaseCloseCancelParams): Promise<ResultObj> => await allFetch('WorkOrder/WorkOrderMasterCaseCloseCancel', obj);
    WorkOrderMasterUpdate = async (obj: WorkOrderMasterUpdateParams): Promise<ResultObj> => await allFetch('WorkOrder/WorkOrderMasterUpdate', obj);
    WorkOrderTurnoverRepairDetailCreate = async (obj: WorkOrderTurnoverRepairDetailCreateParams): Promise<ResultObj> => await allFetch('WorkOrder/WorkOrderTurnoverRepairDetailCreate', obj);
    WorkOrderTurnoverRepairDetailSearch = async (obj: WorkOrderTurnoverRepairDetailSearchParams): Promise<ResultObj<WorkOrderTurnoverRepairDetailSearchPesItem[]>> => await allFetch('WorkOrder/WorkOrderTurnoverRepairDetailSearch', obj);
    WorkOrderTurnoverRepairDetailUpdate = async (obj: WorkOrderTurnoverRepairDetailUpdateParams): Promise<ResultObj> => await allFetch('WorkOrder/WorkOrderTurnoverRepairDetailUpdate', obj);
    WorkOrderTurnoverRepairDetailDelete = async (obj: WorkOrderTurnoverRepairDetailDeleteParams): Promise<ResultObj> => await allFetch('WorkOrder/WorkOrderTurnoverRepairDetailDelete', obj);
    WorkOrderMasterInvalid = async (obj: WorkOrderMasterInvalidParams): Promise<ResultObj> => await allFetch('WorkOrder/WorkOrderMasterInvalid', obj);
    WorkOrderPayLogSearch = async (obj: WorkOrderPayLogSearchParams): Promise<ResultObj<WorkOrderPayLogSearchResItem[]>> => await allFetch('WorkOrder/WorkOrderPayLogSearch', obj);
    WorkOrderPayLogCreate = async (obj: WorkOrderPayLogCreateParams): Promise<ResultObj> => await allFetch('WorkOrder/WorkOrderPayLogCreate', obj);
    WorkOrderPayLogUpdate = async (obj: WorkOrderPayLogUpdateParams): Promise<ResultObj> => await allFetch('WorkOrder/WorkOrderPayLogUpdate', obj);
    WorkOrderPayLogDelete = async (obj: WorkOrderPayLogDeleteParams): Promise<ResultObj> => await allFetch('WorkOrder/WorkOrderPayLogDelete', obj);
    WorkOrderPayLogDetailCreate = async (obj: WorkOrderPayLogDetailCreateParams): Promise<ResultObj> => await allFetch('WorkOrder/WorkOrderPayLogDetailCreate', obj);
    WorkOrderPayLogDetailDelete = async (obj: WorkOrderPayLogDetailDeleteParams): Promise<ResultObj> => await allFetch('WorkOrder/WorkOrderPayLogDetailDelete', obj);
    WorkOrderPayLogDetailSearch = async (obj: WorkOrderPayLogDetailSearchParams): Promise<ResultObj<WorkOrderPayLogDetailSearchResItem[]>> => await allFetch('WorkOrder/WorkOrderPayLogDetailSearch', obj);
    WorkOrderExtFinish = async (obj: WorkOrderExtFinishParams) => await allFetch('WorkOrder/WorkOrderExtFinish', obj);
    WorkOrderExtFinishCancel = async (obj: WorkOrderExtFinishCancelParams) => await allFetch('WorkOrder/WorkOrderExtFinishCancel', obj);
    WorkOrderMasterComplete = async (obj: WorkOrderMasterCompleteParams): Promise<ResultObj> => await allFetch('WorkOrder/WorkOrderMasterComplete', obj);
    WorkOrderMasterCaseClose = async (obj: WorkOrderMasterCaseCloseParams): Promise<ResultObj> => await allFetch('WorkOrder/WorkOrderMasterCaseClose', obj);
    WorkOrderStockRequisitionCreate = async (obj: WorkOrderStockRequisitionCreateParams): Promise<ResultObj> => await allFetch('WorkOrder/WorkOrderStockRequisitionCreate', obj);
    WorkOrderStockRequisitionSearch = async (obj: WorkOrderStockRequisitionSearchParams): Promise<ResultObj<WorkOrderStockRequisitionSearchResItem[]>> => await allFetch('WorkOrder/WorkOrderStockRequisitionSearch', obj);
    WorkOrderStockRequisitionUpdate = async (obj: WorkOrderStockRequisitionUpdateParams): Promise<ResultObj> => await allFetch('WorkOrder/WorkOrderStockRequisitionUpdate', obj);
    WorkOrderStockRequisitionRecordPrintTime = async (obj: WorkOrderStockRequisitionRecordPrintTimeParams): Promise<ResultObj> => await allFetch('WorkOrder/WorkOrderStockRequisitionRecordPrintTime', obj);
    WorkOrderStockRequisitionInvalid = async (obj: WorkOrderStockRequisitionInvalidParams): Promise<ResultObj> => await allFetch('WorkOrder/WorkOrderStockRequisitionInvalid', obj);
    WorkOrderStockRequisitionDelete = async (obj: WorkOrderStockRequisitionDeleteParams): Promise<ResultObj> => await allFetch('WorkOrder/WorkOrderStockRequisitionDelete', obj);
    RepairListDetailCreate = async (obj: RepairListDetailCreateParams): Promise<ResultObj> => await allFetch('WorkOrder/RepairListDetailCreate', obj);
    RepairListDetailSearch = async (obj: RepairListDetailSearchParams): Promise<ResultObj<RepairListDetailSearchResItem[]>> => await allFetch('WorkOrder/RepairListDetailSearch', obj);
    RepairListDetailCheck = async (obj: RepairListDetailCheckParams): Promise<ResultObj<RepairListDetailCheckResItem>> => await allFetch('WorkOrder/RepairListDetailCheck', obj);
    RepairListDetailUpdate = async (obj: RepairListDetailUpdateParams): Promise<ResultObj> => await allFetch('WorkOrder/RepairListDetailUpdate', obj);
    RepairListDetailDelete = async (obj: RepairListDetailDeleteParams): Promise<ResultObj> => await allFetch('WorkOrder/RepairListDetailDelete', obj);
    RepairListDetailDispatch = async (obj: RepairListDetailDispatchParams): Promise<ResultObj> => await allFetch('WorkOrder/RepairListDetailDispatch', obj);
    RepairListDetailDispatchConstruct = async (obj: RepairListDetailDispatchConstructParams): Promise<ResultObj> => await allFetch('WorkOrder/RepairListDetailDispatchConstruct', obj);
    RepairListDetailDispatchRest = async (obj: RepairListDetailDispatchRestParams): Promise<ResultObj> => await allFetch('WorkOrder/RepairListDetailDispatchRest', obj);
    RepairListDetailDispatchFinish = async (obj: RepairListDetailDispatchFinishParams): Promise<ResultObj> => await allFetch('WorkOrder/RepairListDetailDispatchFinish', obj);
    RepairListDetailReport = async (obj: WorkOrderMasterSearchParams): Promise<ResultObj> => await allFetch('Report/RepairListDetailReport', obj);
    WorkOrderCreditNoteMasterCreate = async (obj: WorkOrderCreditNoteMasterCreateParams): Promise<ResultObj<string>> => await allFetch("WorkOrder/WorkOrderCreditNoteMasterCreate", obj);
    WorkOrderCreditNoteMasterUpdate = async (obj: WorkOrderCreditNoteMasterUpdateParams): Promise<ResultObj> => await allFetch("WorkOrder/WorkOrderCreditNoteMasterUpdate", obj);
    WorkOrderCreditNoteMasterSearch = async (obj: WorkOrderCreditNoteMasterSearchParams): Promise<ResultObj<WorkOrderCreditNoteMasterSearchResItem[]>> => await allFetch("WorkOrder/WorkOrderCreditNoteMasterSearch", obj);
    WorkOrderCreditNoteDetailCreate = async (obj: WorkOrderCreditNoteDetailCreateParams): Promise<ResultObj> => await allFetch("WorkOrder/WorkOrderCreditNoteDetailCreate", obj);
    WorkOrderCreditNoteDetailSearch = async (obj: WorkOrderCreditNoteDetailSearchParams): Promise<ResultObj<WorkOrderCreditNoteDetailSearchResItem[]>> => await allFetch("WorkOrder/WorkOrderCreditNoteDetailSearch", obj);
    WorkOrderCreditNoteMasterComplete = async (obj: WorkOrderCreditNoteMasterCompleteParams): Promise<ResultObj> => await allFetch("WorkOrder/WorkOrderCreditNoteMasterComplete", obj);
    RepairListDetailAfterPayLogUpdate = async (obj: RepairListDetailAfterPayLogUpdateParams): Promise<ResultObj> => await allFetch("WorkOrder/RepairListDetailAfterPayLogUpdate", obj);
    WorkOrderMasterTotalInsuranceAndUpdateRLD = async (obj: WorkOrderMasterTotalInsuranceAndUpdateRLDParams): Promise<ResultObj> => await allFetch("WorkOrder/WorkOrderMasterTotalInsuranceAndUpdateRLD", obj);
    WorkOrderCreditNoteCancelCreate = async (obj: WorkOrderCreditNoteCancelCreateParams): Promise<ResultObj> => await allFetch("WorkOrder/WorkOrderCreditNoteCancelCreate", obj);
    WorkOrderCreditNoteCancelSearch = async (obj: WorkOrderCreditNoteCancelSearchParams): Promise<ResultObj<WorkOrderCreditNoteCancelSearchResItem[]>> => await allFetch("WorkOrder/WorkOrderCreditNoteCancelSearch", obj);
    WorkOrderStockRequisitionCommoditySearch = async (obj: WorkOrderStockRequisitionCommoditySearchParams): Promise<ResultObj<WorkOrderStockRequisitionCommoditySearchResItem[]>> => await allFetch("WorkOrder/WorkOrderStockRequisitionCommoditySearch", obj);
    WorkOrderMaintainStatusLogCreate = async (obj: WorkOrderMaintainStatusLogCreateParams): Promise<ResultObj> => await this.emptyAdapter();
    WorkOrderMaintainStatusLogUpdate = async (obj: WorkOrderMaintainStatusLogUpdateParams): Promise<ResultObj> => await this.emptyAdapter();
    WorkOrderMaintainStatusLogSearch = async (obj: WorkOrderMaintainStatusLogSearchParams): Promise<ResultObj<WorkOrderMaintainStatusLogSearchResItem[]>> => await this.emptyAdapter();
    WorkOrderPredictDeliveryLogCreate = async (obj: WorkOrderPredictDeliveryLogCreateParams): Promise<ResultObj> => await this.emptyAdapter();
    WorkOrderPredictDeliveryLogUpdate = async (obj: WorkOrderPredictDeliveryLogUpdateParams): Promise<ResultObj> => await this.emptyAdapter();
    WorkOrderPredictDeliveryLogSearch = async (obj: WorkOrderPredictDeliveryLogSearchParams): Promise<ResultObj<WorkOrderPredictDeliveryLogSearchResItem[]>> => await this.emptyAdapter();
    WorkOrderMasterTempStop = async (obj: WorkOrderMasterTempStopParams): Promise<ResultObj> => await this.emptyAdapter();
    WorkOrderMasterTempStopCancel = async (obj: WorkOrderMasterTempStopCancelParams): Promise<ResultObj> => await this.emptyAdapter();
}







/**
 * @description 工單建立參數
 * @param {string} wom_code 工單號碼
 * @param {string} wom_type 工單類別
 * @param {string} wom_category 工單內外帳類別
 * @param {string} payment_DEP 請款部門(代碼DEP)
 * @param {string} user_id 使用者流水號
 * @param {string} position_id 據點流水號
 * @param {string} customer_id 車主流水號
 * @param {string} customer_name 車主姓名
 * @param {string} contacter_name 聯絡人姓名
 * @param {string} contacter_phone 聯絡人電話
 * @param {string} contacter_email 聯絡人信箱
 * @param {string} contacter_city 聯絡人城市
 * @param {string} contacter_area 聯絡人區域
 * @param {string} contacter_address 聯絡人地址
 * @param {string} car_id 車流水號
 * @param {string} mileage 里程
 * @param {string} time_enter 進廠時間
 * @param {string} insurance_company_id 保險公司流水號
 * @param {string} insurance_company_name 保險公司名稱
 * @param {string} insurance_case_number 保險案號
 * @param {string} insurance_company_contacter_id 保險公司聯絡人流水號
 * @param {string} insurance_company_contacter_name 保險公司聯絡人姓名
 * @param {string} note 備註
 * @param {string} suggestion 建議事項
 * @param {string} is_panelpaint 是否鈑烤
 * @param {string} is_dashboard_visible 是否顯示在進度看板上
 * @param {string} maintain_status 維修狀態
 * @param {string} predict_delivery_datetime 預估交車時間
 */
export interface WorkOrderMasterCreateParams {
    wom_code: string;
    wom_type: string;
    wom_category: "內部" | "外部" | "";
    payment_DEP: string;
    user_id: string;
    customer_id: string | null;
    customer_name: string | null;
    contacter_name: string;
    contacter_phone: string;
    contacter_email: string;
    contacter_city: string;
    contacter_area: string;
    contacter_address: string;
    car_id: string | null;
    mileage: string;
    time_enter: string;
    insurance_company_id: string;
    insurance_company_name: string;
    insurance_case_number: string;
    insurance_company_contacter_id: string;
    insurance_company_contacter_name: string;
    note: string;
    suggestion: string;
    position_id: string;
    is_dashboard_visible: "Y" | "N";
    maintain_status: string;
    predict_delivery_datetime: string;
    is_panelpaint: "Y" | "N";
}

/**
 * @description  工單查詢參數
 * @param {string} wom_code 工單號碼
 * @param {string} status 狀態們
 * @param {string} user_id 使用者流水號
 * @param {string} position_id 據點流水號
 * @param {string} customer_id 車主流水號
 * @param {string} car_id 車流水號
 * @param {string} license_plate 車牌號碼
 * @param {string} time_enter_start 進廠時間起
 * @param {string} time_enter_end 進廠時間迄
 * @param {string} time_leave_start 離廠時間起
 * @param {string} time_leave_end 離廠時間迄
 * @param {string} insurance_company_id 保險公司流水號
 * @param {string} is_complete 是否完工
 * @param {string} complete_time_start 完工時間起
 * @param {string} complete_time_end 完工時間迄
 * @param {string} is_caseclose 是否結案
 * @param {string} caseclose_time_start 結案時間起
 * @param {string} caseclose_time_end 結案時間迄
 * @param {string} is_invalid 是否作廢
 * @param {string} invalid_time_start 作廢時間起
 * @param {string} invalid_time_end 作廢時間迄
 * @param {string} invoice_number 發票號碼
 * @param {string} is_panelpaint 是否鈑烤
 * @param {string} excel 是否匯出
 * @param {string} page 頁碼
 * @param {string} num_per_page 一頁幾筆
 * @param {string} is_dashboard_visible 是否顯示在進度看板上
 * @param {string[]} maintain_statuses 維修狀態們
 * @param {string} predict_delivery_datetime_start 預估交車時間起
 * @param {string} predict_delivery_datetime_end 預估交車時間迄
 */
export interface WorkOrderMasterSearchParams {
    wom_code?: string
    wom_category?: "內部" | "外部" | ""
    status?: string[]
    user_id?: string
    position_id?: string
    customer_id?: string
    car_id?: string
    license_plate?: string
    time_enter_start?: string
    time_enter_end?: string
    time_leave_start?: string
    time_leave_end?: string
    insurance_company_id?: string
    is_complete?: string
    complete_time_start?: string
    complete_time_end?: string
    is_caseclose?: string
    caseclose_time_start?: string
    caseclose_time_end?: string
    is_invalid?: string
    invalid_time_start?: string
    invalid_time_end?: string
    invoice_number?: string
    is_panelpaint?: string
    excel?: string
    page?: number
    num_per_page?: number
    is_dashboard_visible?: string
    maintain_statuses?: string[]
    predict_delivery_datetime_start?: string
    predict_delivery_datetime_end?: string
}

/**
 * @description  工單查詢結果
 */
export interface WorkOrderMasterSearchResItem {
    cre_time: string;
    wom_id: string;
    wom_code: string;
    wom_category: string,
    wom_type: string;
    status: string;
    complete_user_username: string;
    complete_user_name: string;
    complete_time: string;
    caseclose_user_username: string;
    caseclose_user_name: string;
    caseclose_time: string;
    invalid_user_username: string;
    invalid_user_name: string;
    invalid_time: string;
    invalid_reason: string;
    user_id: string;
    username: string;
    name: string;
    user_name: string;
    position_id: string;
    position_name: string;
    customer_id: string;
    customer_name: string;
    contacter_name: string;
    contacter_phone: string;
    contacter_email: string;
    contacter_city: string;
    contacter_area: string;
    contacter_address: string;
    car_id: string;
    vin_no: string;
    license_plate: string;
    last_mileage: number;
    mileage: number;
    ctf_id: number;
    ct_id: number;
    car_type: string;
    format_code: string;
    format_name: string;
    year: number;
    last_time_enter: string;
    time_enter: string;
    time_leave: string;
    insurance_company_id: string;
    insurance_company_name: string;
    insurance_case_number: string;
    insurance_company_contacter_id: number;
    insurance_company_contacter_name: string;
    total_price: number;
    rld_total_price: number;
    rld_discount_price: number;
    insurance_price: number;
    insurance_price_tax: number;
    insurance_price_tax_include: number;
    insurance_sum_price_tax: number;
    insurance_sum_price_tax_include: number;
    deductible_price: number;
    deductible_price_tax: number;
    deductible_price_tax_include: number;
    deductible_sum_price_tax: number;
    deductible_sum_price_tax_include: number;
    discount: number;
    discount_price: number;
    note: string;
    suggestion: string;
    is_panelpaint: string;
    ext_finish_time: string;
    predict_delivery_datetime: string;
    maintain_status: string;
    payment_DEP: string;
    is_dashboard_visible: string;
}

/**
 * @description 工單細項參數
 * @param {string} wom_id 工單流水號
 */
export interface WorkOrderMasterDetailParams {
    wom_id: string;
}

/**
 * @description 工單細項結果
 */
export interface WorkOrderMasterDetailResItem {
    DEP_name: string;
    car_id: string;
    car_type: string;
    caseclose_time: string;
    caseclose_user_name: string;
    caseclose_user_username: string;
    complete_time: string;
    complete_user_name: string;
    complete_user_username: string;
    contacter_address: string;
    contacter_area: string;
    contacter_city: string;
    contacter_email: string;
    contacter_name: string;
    contacter_phone: string;
    cre_time: string;
    ct_id: string;
    ctf_id: string;
    customer_id: string;
    customer_name: string;
    deductible_balance: string;
    deductible_price: string;
    deductible_price_tax: string;
    deductible_price_tax_include: string;
    deductible_sum_price_tax: string;
    deductible_sum_price_tax_include: string;
    discount: string;
    discount_price: string;
    ext_finish_time: string;
    external_key: string;
    format_code: string;
    format_name: string;
    insurance_balance: string;
    insurance_case_number: string;
    insurance_company_contacter_id: string;
    insurance_company_contacter_name: string;
    insurance_company_id: string;
    insurance_company_name: string;
    insurance_price: string;
    insurance_price_tax: string;
    insurance_price_tax_include: string;
    insurance_sum_price_tax: string;
    insurance_sum_price_tax_include: string;
    invalid_reason: string;
    invalid_time: string;
    invalid_user_name: string;
    invalid_user_username: string;
    is_panelpaint: string;
    last_mileage: string;
    last_time_enter: string;
    license_plate: string;
    mileage: string;
    name: string;
    note: string;
    payment_DEP: string;
    position_id: string;
    position_name: string;
    rld_discount_price: string;
    rld_total_price: string;
    status: string;
    suggestion: string;
    time_enter: string;
    time_leave: string;
    total_price: string;
    user_id: string;
    username: string;
    user_name: string;
    vin_no: string;
    wom_category: string;
    wom_code: string;
    wom_id: string;
    wom_type: string;
    wom_type_name: string;
    year: string;
}


/**
 * @description 工單完工取消參數
 * @param {string} wom_id 工單流水號
 */
export interface WorkOrderMasterCompleteCancelParams {
    wom_id: string;
}

/**
 * @description 工單結案取消參數
 * @param {string} wom_id 工單流水號
 */
export interface WorkOrderMasterCaseCloseCancelParams {
    wom_id: string;
}

/**
 * @description 工單修改參數
 * @param {string} wom_id 工單流水號
 * @param {string} wom_code 工單號碼
 * @param {string} user_id 使用者流水號
 * @param {string} position_id 據點流水號
 * @param {string} customer_id 車主流水號
 * @param {string} customer_name 車主姓名
 * @param {string} contacter_name 聯絡人姓名
 * @param {string} contacter_phone 聯絡人電話
 * @param {string} contacter_email 聯絡人電子郵件
 * @param {string} contacter_address 聯絡人地址
 * @param {string} car_id 車流水號
 * @param {string} discount 折扣
 * @param {string} mileage 里程數
 * @param {string} time_enter 進廠時間
 * @param {string} insurance_company_id 保險公司流水號
 * @param {string} insurance_company_name 保險公司名稱
 * @param {string} insurance_case_number 保險案號
 * @param {string} insurance_company_contacter_id 保險公司聯絡人流水號
 * @param {string} insurance_company_contacter_name 保險公司聯絡人姓名
 * @param {string} note 備註
 * @param {string} suggestion 建議事項
 * @param {string} maintain_status 車輛狀態
 * @param {string} predict_delivery_datetime 預計交車時間
 * @param {string} is_dashboard_visible 是否顯示在進度看板上
 */
export interface WorkOrderMasterUpdateParams {
    wom_id: string;
    wom_code: string;
    user_id: string;
    position_id?: string;
    customer_id?: string;
    customer_name?: string;
    contacter_name?: string;
    contacter_phone?: string;
    contacter_email?: string;
    contacter_address?: string;
    car_id: string;
    discount?: string;
    mileage?: string;
    time_enter?: string;
    insurance_company_id?: string;
    insurance_company_name?: string;
    insurance_case_number?: string;
    insurance_company_contacter_id?: string;
    insurance_company_contacter_name?: string;
    note?: string;
    suggestion?: string;
    maintain_status?: string;
    predict_delivery_datetime?: string;
    is_dashboard_visible?: string;
}

/**
 * @description 工單交修項目新建參數
 * @param {string} wom_id 工單流水號
 * @param {string} trs_id 交修項目流水號
 * @param {string} trs_code 交修項目代碼
 * @param {string} trs_name 交修項目名稱
 */
export interface WorkOrderTurnoverRepairDetailCreateParams {
    wom_id: string;
    trs_id: string;
    trs_code: string;
    trs_name: string;
}

/**
 * @description 工單交修項目查詢參數
 * @param {string} wom_id 工單流水號
 */
export interface WorkOrderTurnoverRepairDetailSearchParams {
    wom_id: string;
}

/**
 * @description 工單交修項目查詢結果
 */
export interface WorkOrderTurnoverRepairDetailSearchPesItem {
    trs_code: string;
    trs_id: string;
    trs_name: string;
    wom_id: string;
    wotrd_id: string;
}

/**
 * @description 工單交修項目修改參數
 * @param {string} wotrd_id 工單交修項目流水號
 * @param {string} trs_id 交修項目流水號
 * @param {string} trs_code 交修項目代碼
 * @param {string} trs_name 交修項目名稱
 */
export interface WorkOrderTurnoverRepairDetailUpdateParams {
    wotrd_id: string;
    trs_id: string;
    trs_code: string;
    trs_name: string;
}

/**
 * @description 工單交修項目刪除參數
 * @param {string} wotrd_id 工單交修項目流水號
 */
export interface WorkOrderTurnoverRepairDetailDeleteParams {
    wotrd_id: string;
}

/**
 * @description 工單作廢參數
 * @param {string} wom_id 工單流水號
 * @param {string} invalid_reason 作廢原因
 */
export interface WorkOrderMasterInvalidParams {
    wom_id: string;
    invalid_reason: string;
}

/**
 * @description 工單付款記錄查詢參數
 * @param {string} pl_id 付款紀錄流水號
 * @param {string} wom_id 工單流水號
 * @param {string} is_insurance 是否保險支付
 * @param {string} pay_method 付款方式
 * @param {string} pay_date_start 付款日期起
 * @param {string} pay_date_end 付款日期迄
 * @param {string} invoice_number 發票號碼
 * @param {string} tax_id 統編號碼
 * @param {string} invoice_date_start 發票日期起
 * @param {string} invoice_date_end 發票日期迄
 */
export interface WorkOrderPayLogSearchParams {
    pl_id?: string;
    wom_id?: string;
    is_insurance?: string;
    pay_method?: string;
    pay_date_start?: string;
    pay_date_end?: string;
    invoice_number?: string;
    is_system_invoice?: string;
    tax_id?: string;
    invoice_date_start?: string;
    invoice_date_end?: string;
}

export interface WorkOrderPayLogSearchResItem {
    cre_time: string;
    credit_card_number: string;
    credit_card_type: string;
    credit_card_type_name: string;
    discount: string;
    heading: string;
    insurance_company_code: string;
    insurance_company_id: string;
    invoice_date: string;
    invoice_discount_price: string;
    invoice_discount_time: string;
    invoice_discount_user_id: string;
    invoice_discount_user_name: string;
    invoice_number: string;
    invoice_status: string;
    is_insurance: string;
    is_system_invoice: string;
    master_id: string;
    note: string;
    pay_date: string;
    pay_method: string;
    pay_method_name: string;
    payer_name: string;
    pl_id: string;
    price: string;
    select_ids: string;
    tax_id: string;
}

/** 工單付款記錄新增
 * @param wom_id 工單流水號
 * @param is_insurance 是否保險
 * @param price 金額
 * @param discount 折扣
 * @param payer_name 付款人名稱
 * @param pay_method 付款方式
 * @param pay_date 付款日期
 * @param invoice_number 發票號碼
 * @param note 備註
 * @param is_system_invoice 是否系統開發票
 * @param credit_card_type 信用卡別
 * @param credit_card_number 信用卡號
 * @param insurance_company_id 保險公司流水號
 * @param tax_id 統編號碼
 * @param heading 抬頭
 * @param invoice_date 發票日期
 */
export interface WorkOrderPayLogCreateParams {
    wom_id: string;
    is_insurance: string;
    price?: string;
    discount?: string;
    payer_name?: string;
    pay_method: string;
    pay_date?: string;
    invoice_number?: string;
    note?: string;
    is_system_invoice: string;
    credit_card_type?: string;
    credit_card_number?: string;
    insurance_company_id?: string;
    tax_id?: string;
    heading?: string;
    invoice_date?: string;
}

/**
 * @description 工單付款記錄修改參數
 * @param {string} pl_id 付款記錄流水號
 * @param {string} is_insurance 是否保險支付
 * @param {string} price 金額
 * @param {string} discount 折扣
 * @param {string} payer_name 付款名稱
 * @param {string} pay_method 付款方式
 * @param {string} pay_date 付款日期
 * @param {string} invoice_number 發票號碼
 * @param {string} note 備註
 * @param {string} credit_card_type 信用卡別
 * @param {string} credit_card_number 信用卡號
 * @param {string} invoice_status 付款狀態
 * @param {string} insurance_company_id 保險公司流水號
 * @param {string} tax_id 統編號碼
 * @param {string} heading 抬頭
 * @param {string} invoice_date 發票日期
 */
export interface WorkOrderPayLogUpdateParams {
    pl_id: string;
    is_insurance: string;
    price?: string;
    discount?: string;
    payer_name?: string;
    pay_method: string;
    pay_date?: string;
    invoice_number?: string;
    note?: string;
    credit_card_type?: string;
    credit_card_number?: string;
    invoice_status?: string;
    insurance_company_id?: string;
    tax_id?: string;
    heading?: string;
    invoice_date?: string;
}

/**
 * @description 工單付款記錄刪除參數
 * @param {string} pl_id 付款記錄流水號
 */
export interface WorkOrderPayLogDeleteParams {
    pl_id: string;
}

/** 新建付款紀錄細項
 * @param {string} pl_id 付款記錄流水號
 * @param {string} rld_id 維修清單細項流水號
 * @param {string} inv_type 收費項目名稱
 * @param {string} amount 項目小計
 * @param {string} note 備註
 */
export interface WorkOrderPayLogDetailCreateParams {
    pl_id: string;
    rld_id: string;
    inv_type?: string;
    amount?: number;
    note?: string;
}

/** 刪除付款紀錄細項
 * @param {string} pld_id 付款紀錄細項流水號
 */
export interface WorkOrderPayLogDetailDeleteParams {
    pld_id: string;
}

/** 查詢付款紀錄細項
 * @param {string} pl_id 付款記錄流水號
 * @param {string} pld_id 付款紀錄細項流水號
 * @param {string} wom_id 工單流水號 
 * @param {string} rld_id 維修清單細項流水號
 * @param {string} inv_type 收費項目名稱
 * @param {string} is_insurance 是否保險
 * @param {string} invoice_number 發票號碼
 * @param {string} is_system_invoice 是否系統開發票
 * @param {string} note 備註
 */
export interface WorkOrderPayLogDetailSearchParams {
    pl_id?: string;
    pld_id?: string;
    wom_id?: string;
    rld_id?: string;
    inv_type?: string;
    is_insurance?: string;
    invoice_number?: string;
    is_system_invoice?: string;
    note?: string;
}

/** 查詢付款紀錄細項 (回傳) 
 * @param wocnd_total_count 折讓數量
 * @param wocnd_total_price 折讓金額
 * @param balance_count 剩下可以折的數量
 * @param balance_price 剩下可以折的金額
*/
export interface WorkOrderPayLogDetailSearchResItem {
    amount: number;
    inv_type: string;
    inv_type_name: string;
    invoice_number: string;
    invoice_status: string;
    is_insurance: string;
    is_system_invoice: string;
    note: string;
    pl_id: string;
    pld_id: string;
    rld_count: string;
    rld_detail_id: string;
    rld_id: string;
    rld_master_id: string;
    rld_master_type: string;
    rld_rld_code: string;
    rld_rld_name: string;
    rld_rld_type: string;
    wom_id: string;

    wocnd_total_count: string;
    wocnd_total_price: string;
    balance_count: string;
    balance_price: string;

}

export interface WorkOrderExtFinishParams {
    wom_id: string;
}

export interface WorkOrderExtFinishCancelParams {
    wom_id: string;
}

/**
 * @description 工單完工參數
 * @param {string} wom_id 工單流水號
 */
export interface WorkOrderMasterCompleteParams {
    wom_id: string;
}

/**
 * @description 工單結案參數
 * @param {string} wom_id 工單流水號
 */
export interface WorkOrderMasterCaseCloseParams {
    wom_id: string;
}

/**
 * @description 工單庫存領用新建參數
 * @param {string} wom_id 工單流水號
 * @param {string} qcd_id 估價單商品細項流水號
 * @param {string} wotrd_id 工單交修項目流水號
 * @param {string} stock_id 庫存流水號
 * @param {number} count 數量
 * @param {number} total_price 總金額
 * @param {string} need_calc_stock 是否計算庫存
 */
export interface WorkOrderStockRequisitionCreateParams {
    wom_id: string;
    qcd_id: string;
    wotrd_id: number;
    stock_id: string;
    count: number;
    total_price: number;
    discount: number;
    need_calc_stock: string;
}

/**
 * @description 工單庫存領用查詢參數
 * @param {string} wom_id 工單流水號
 */
export interface WorkOrderStockRequisitionSearchParams {
    wom_id: string;
    is_valid?: string;
    is_printed?: string;
}

/**
 * @description 工單庫存領用查詢結果
 */
export interface WorkOrderStockRequisitionSearchResItem {
    cre_time: string
    wosr_id: string,
    wotrd_id: number
    wom_id: string,
    stock_id: string,
    convert_unit_name: string,
    convert_count: number
    basic_unit_name: string,
    basic_count: number
    sale_price: number,
    uc_id: string,
    commodity_code: string,
    commodity_name: string,
    warehouse_id: string,
    warehouse_name: string,
    warehouse_storage_space: string,
    count: number
    total_price: number,
    total_cost: number
    discount: number
    discount_price: number
    need_calc_stock: string,
    qcd_id: string,
    qcd_count: number
    print_time: string
    is_valid: string
}


/**
 * @description 工單庫存領用修改參數
 * @param {string} wosr_id 工單庫存領用流水號
 * @param {string} wotrd_id 工單交修項目流水號
 * @param {number} count 數量
 * @param {string} need_calc_stock 是否計算庫存
 */
export interface WorkOrderStockRequisitionUpdateParams {
    wosr_id: string,
    wotrd_id?: number,
    count?: number,
    total_price?: number,
    discount?: number,
    need_calc_stock?: string
}

/**
 * @description 工單庫存領用刪除參數
 * @param {string} wosr_id 工單庫存領用流水號
 */
export interface WorkOrderStockRequisitionRecordPrintTimeParams {
    wosr_id: string;
}

/**
 * @description 工單庫存領用刪除參數
 * @param {string} wosr_id 工單庫存領用流水號
 */
export interface WorkOrderStockRequisitionInvalidParams {
    wosr_id: string;
}

/**
 * @description 工單庫存領用刪除參數
 * @param {string} wosr_id 工單庫存領用流水號
 */
export interface WorkOrderStockRequisitionDeleteParams {
    wosr_id: string;
}

/**
 * @description 維修清單細項新建參數
 * @param {string} wom_id 工單流水號
 * @param {string} wotrd_id 工單交修項目流水號
 * @param {string} master_type 來源類型
 * @param {string} master_id 來源主項流水號 估價單主項ID、商品ID
 * @param {string} detail_id 來源細項流水號 估價單細項ID、維修清單A單ID、收費項目細項ID
 * @param {string} rld_type 維修清單細項類別 板金、烤漆、零件
 * @param {string} rld_code 維修清單細項代號
 * @param {string} rld_name 維修清單細項名稱
 * @param {number} work_hours_setting 標準工時
 * @param {number} count 維修清單細項數量
 * @param {number} total_price 總金額
 * @param {number} insurance_price 保險金額
 * @param {number} deductible_price 自負額
 * @param {number} discount 折扣
 * @param {string} note 備註
 */
export interface RepairListDetailCreateParams {
    wom_id: string;
    wotrd_id?: number;
    master_type: string;
    master_id: string;
    detail_id: string;
    rld_type: string;
    rld_code: string;
    rld_name: string;
    work_hours_setting: number;
    count: number;
    total_price: number;
    insurance_price: number;
    deductible_price: number;
    discount: number;
    note: string;
    rld_category: string;
}

/**
 * @description 維修清單細項查詢參數
 * @param wom_id 工單流水號
 * @param master_type 來源類型
 * @param rld_category 內外帳類型
 * @param rld_type 維修清單細項類別 板金、烤漆、零件
 * @param master_id 來源主項流水號
 * @param dispatch_user_id 被派工使用者流水號
 * @param dispatch_company_id 被派工外包廠商流水號
 * @param dispatch_statuses 派工狀態們
 * @param pl_id 付款記錄流水號
 * @param is_pay 是否已付款
 * @param is_before_invoice 查詢"綁定"功能上線前未綁定發票之項目
 */
export interface RepairListDetailSearchParams {
    wom_id: string;
    master_type?: string;
    rld_category?: string;
    rld_type?: string;
    master_id?: string;
    dispatch_user_id?: string;
    dispatch_company_id?: string;
    dispatch_statuses?: string[];
    pl_id?: string;
    is_pay?: string;
    is_before_invoice?: 'Y' | 'N';
}

/** 維修清單細項查詢結果 */
export interface RepairListDetailSearchResItem {
    cre_userid: string,
    cre_time: string
    upd_userid: string,
    upd_time: string
    rld_id: string,
    wotrd_id: number
    wom_id: string,
    wom_code: string,
    master_type: string,
    master_id: string,
    detail_id: string,
    rld_category: string,
    rld_type: string,
    rld_code: string,
    rld_name: string,
    pl_id: string,
    inv_type: string,
    inv_type_name: string,
    dispatch_status: string,
    dispatch_user_id: string,
    dispatch_user_username: string,
    dispatch_user_name: string,
    dispatch_company_id: string,
    dispatch_company_name: string,
    dispatch_company_cost: number
    dispatch_time: string
    finish_time: string
    work_hours_setting: number
    work_hours_actual: number
    count: number
    total_price: number
    insurance_price: number
    deductible_price: number
    discount: number
    discount_price: number
    note: string,
    insurance_price_tax: number
    insurance_price_tax_include: number
    deductible_price_tax: number
    deductible_price_tax_include: number
    sale_price: number
}


/** 維修清單檢查參數
 * @param {string} wom_id 工單流水號
 */
export interface RepairListDetailCheckParams {
    wom_id: string;
}
export interface RepairListDetailCheckResItem {
    dt_qcd: RepairListDetailCheckResItem_qcd[];
    dt_qid: RepairListDetailCheckResItem_qid[];
    dt_rlds: RepairListDetailCheckResItem_rld[];
}

/** 估價零件 quotation commodity detail */
export type RepairListDetailCheckResItem_qcd = {
    commodity_code: string;
    commodity_id: string;
    commodity_name: string;
    commodity_type: string;
    count: number;
    deductible_price: number;
    discount: number;
    discount_price: number;
    insurance_price: number;
    master_type: string;
    note: string;
    qm_id: string;
    qcd_id: string;
    total_price: number;
}

/** 估價項目 quotation item detail */
export type RepairListDetailCheckResItem_qid = {
    deductible_price: number;
    discount: number;
    discount_price: number;
    insurance_price: number;
    master_type: string;
    note: string;
    qid_id: string;
    qis_code: string;
    qis_name: string;
    qis_type: string;
    qm_id: string;
    total_price: number;
    work_hours_setting: number;
}

/** 維修清單細項 repair list detail */
export type RepairListDetailCheckResItem_rld = {
    count: number;
    cre_time: string;
    deductible_price: number;
    deductible_price_tax: number;
    deductible_price_tax_include: number;
    detail_id: string;
    discount: number;
    discount_price: number;
    dispatch_company_cost: number;
    dispatch_company_id: string;
    dispatch_company_name: string;
    dispatch_status: string;
    dispatch_time: string;
    dispatch_user_id: string;
    dispatch_user_name: string;
    dispatch_user_username: string;
    finish_time: string;
    insurance_price: number;
    insurance_price_tax: number;
    insurance_price_tax_include: number;
    master_id: string;
    master_type: string;
    note: string;
    rld_category: string;
    rld_code: string;
    rld_id: string;
    rld_name: string;
    rld_type: string;
    sale_price: number;
    total_price: number;
    user_cre_time: string;
    wom_code: string;
    wom_id: string;
    work_hours_actual: number;
    work_hours_setting: number;
    wotrd_id?: number;
}

/**
 * @description 維修清單細項修改參數
 * @param {string} rld_id 維修清單細項流水號
 * @param {string} wotrd_id 工單交修項目流水號
 * @param {string} master_type 來源類型
 * @param {string} master_id 來源主項流水號
 * @param {string} detail_id 來源細項流水號
 * @param {string} rld_type 維修清單細項類別
 * @param {string} rld_code 維修清單細項代號
 * @param {string} rld_name 維修清單細項名稱
 * @param {number} work_hours_setting 標準工時
 * @param {number} count 維修清單細項數量
 * @param {number} total_price 總金額
 * @param {number} insurance_price 保險金額
 * @param {number} deductible_price 自負額
 * @param {number} discount 折扣
 * @param {string} note 備註
 */
export interface RepairListDetailUpdateParams {
    rld_id: string;
    wotrd_id?: number;
    master_type: string;
    master_id: string;
    detail_id: string;
    rld_type: string;
    rld_code: string;
    rld_name: string;
    rld_category: string;
    work_hours_setting: number;
    count: number;
    total_price: number;
    insurance_price: number;
    deductible_price: number;
    discount: number;
    note: string;
}

/**
 * @description 維修清單細項刪除參數
 * @param {string} rld_id 維修清單細項流水號
 */
export interface RepairListDetailDeleteParams {
    rld_id: string;
}

/**
 * @description 維修清單們派工參數
 * @param {string} rldd_list 派工項目&使用者們
 */
export interface RepairListDetailDispatchParams {
    rldd_list: Array<RepairListDetailDispatch_objParams>;
}

/**
 * RepairListDetailDispatch_obj
 */
export interface RepairListDetailDispatch_objParams {
    rld_id: string;
    dispatch_user_id?: string;
    dispatch_company_id?: string;
    dispatch_company_cost?: number;
}

/**
 * @description 維修清單們開工參數
 * @param {string} rld_ids 維修清單細項流水號們
 */
export interface RepairListDetailDispatchConstructParams {
    rld_ids: Array<string>;
}

/**
 * @description 維修清單們停工參數
 * @param {string} rld_ids 維修清單細項流水號們
 */
export interface RepairListDetailDispatchRestParams {
    rld_ids: Array<string>;
}

/**
 * @description 維修清單們完成 (參數)
 * @param {string} rld_ids 維修清單細項流水號們
 */
export interface RepairListDetailDispatchFinishParams {
    rld_ids: Array<string>;
}

/**@description 維修清單匯出 Excel (回傳) */
export interface RepairListDetailReportResItem {
    wom_code: string,
    master_type: string,
    dispatch_status: string,
    dispatch_user_name: string,
    rld_code: string,
    rld_type: string,
    rld_name: string,
    work_hours_setting: string,
    count: string,
    deductible_price: string,
    insurance_price: string,
    total_price: string,
    discount_price: string,
    note: string,
}

/** 新建工單折讓單主項
 * @property {string} wom_id 工單流水號
 * @property {string} is_system_invoice 開立類型(系統Y/手動N)
 * @property {string} date 折讓日期
 * @property {string} tax_id 統一編號
 * @property {string} heading 統編抬頭
 * @property {string} note 備註
 * @property {string} reason 折讓理由
 */
export interface WorkOrderCreditNoteMasterCreateParams {
    wom_id: string;
    is_system_invoice?: string;
    date: string;
    tax_id?: string;
    heading?: string;
    note: string;
    reason: string;
}

/** 更新工單折讓單主項
 * @property {string} wocnm_id 工單折讓單流水號
 * @property {string} wom_id 工單流水號
 * @property {string} is_system_invoice 開立類型(系統Y/手動N)
 * @property {string} date 折讓日期
 * @property {string} tax_id 統一編號
 * @property {string} heading 統編抬頭
 * @property {string} note 備註
 * @property {string} reason 折讓理由
 */
export interface WorkOrderCreditNoteMasterUpdateParams {
    wocnm_id: string;
    wom_id: string;
    is_system_invoice: string;
    date: string;
    tax_id?: string;
    heading?: string;
    note: string;
    reason: string;
}


export type WorkOrderCreditNoteMasterSearchResItem = {
    wocnm_id: string;
    wom_id: string;
    date: string;
    note: string;
    reason: string;
    complete_time: string;
    complete_user_name: string;
    complete_user_username: string;
    cre_time: string;
    wom_category: string;
    wom_code: string;
    wom_external_key: string;
    is_system_invoice: string;
    tax_id: string;
    heading: string;
}

/** 查詢工單折讓單主項
 * @property {string} wocnm_id 工單折讓單流水號 (optional)
 * @property {string} wom_id 工單流水號 (optional)
 * @property {string} wom_code 工單號碼 (optional)
 * @property {string} date 折讓日期 (optional)
 * @property {string} is_complete 是否結案 (optional)
 * @property {string} complete_time_start 結案時間起 (optional)
 * @property {string} complete_time_end 結案時間迄 (optional)
 * @property {string} is_system_invoice 開立類型(系統Y/手動N)
 * @property {string} tax_id 統一編號
 * @property {string} heading 統編抬頭
 * @property {number} page 頁碼
 * @property {number} num_per_page 每頁顯示數量
 */
export interface WorkOrderCreditNoteMasterSearchParams {
    wocnm_id?: string;
    wom_id?: string;
    wom_code?: string;
    date?: string;
    is_complete?: string;
    complete_time_start?: string;
    complete_time_end?: string;
    is_system_invoice?: string;
    tax_id?: string;
    heading?: string;
    page: number;
    num_per_page: number;
}

/** 新建工單折讓單細項
 * @property {string} wocnm_id 工單折讓單流水號
 * @property {string} rld_id 來源流水號 (維修清單)
 * @property {string} stock_id 庫存流水號 (optional)
 * @property {string} count 退回數量(如果是收費項目，就給1)
 * @property {string} price 折讓金額(單價)
 * @property {string} pld_id 付款記錄細項流水號
 */
export interface WorkOrderCreditNoteDetailCreateParams {
    wocnm_id: string;
    rld_id: string;
    stock_id: string;
    count: string;
    price: string;
    pld_id?: string;
}

/** 工單折讓單細項 查詢回傳 */
export type WorkOrderCreditNoteDetailSearchResItem = {
    basic_count?: number | null;
    basic_unit_name: string | null;
    ccad_id_1: string | null;
    ccad_id_2: string | null;
    ccad_name_1: string | null;
    ccad_name_2: string | null;
    commodity_code: string | null;
    commodity_id: string | null;
    commodity_name: string | null;
    convert_count?: number | null;
    convert_unit_name: string | null;
    count: string;
    cre_time: string; // ISO 8601 format date-time string
    cre_userid: string;
    inv_type: string;
    inv_type_name: string;
    invoice_number: string;
    is_valid: string;
    master_type: string;
    name: string;
    note: string;
    original_count: string;
    original_price: string;
    original_total_price: string;
    pl_id: string;
    pld_id: string;
    price: string;
    rld_code: string;
    rld_id: string;
    rld_name: string;
    rld_type: string;
    stock_id: string;
    total_price: string;
    uc_id: string | null;
    usercre_time: string; // ISO 8601 format date-time string
    warehouse_id: string | null;
    warehouse_name: string | null;
    warehouse_storage_space: string | null;
    wocnd_id: string;
    wocnm_id: string;
}
/** 工單折讓單細項查詢
 * @property {string} wocnd_id 工單折讓細項流水號 (optional)
 * @property {string} wocnm_id 工單折讓單流水號 (optional)
 * @property {string} rld_id 來源流水號 (optional)
 * @property {string} stock_id 庫存流水號 (optional)
 * @property {string} commodity_code 商品編號 (optional)
 * @property {string} commodity_name 商品名稱 (optional)
 * @property {number} page 頁碼
 * @property {number} num_per_page 每頁顯示數量
 */
export interface WorkOrderCreditNoteDetailSearchParams {
    wocnd_id?: string;
    wocnm_id?: string;
    rld_id?: string;
    stock_id?: string;
    commodity_code?: string;
    commodity_name?: string;
    page: number;
    num_per_page: number;
}

/** 結案工單退單
 * @property {string} wocnm_id 工單折讓單流水號
 */
export interface WorkOrderCreditNoteMasterCompleteParams {
    wocnm_id: string;
}

/** 維修清單出納後更動 (1.僅能更動發票項目及出納ID 2.完工後結案前才能使用)
 * @param rld_id 維修清單細項流水號
 * @param inv_type 收費項目名稱
 * @param pl_id 付款記錄流水號
 */
export interface RepairListDetailAfterPayLogUpdateParams {
    rld_id: string;
    inv_type?: string;
    pl_id: string;
}

/** 工單總核賠計算 (回寫至維修清單) 參數
 * @param wom_id 工單流水號
 * @param total_insurance_tax 總核賠金額(含稅)
 */
export interface WorkOrderMasterTotalInsuranceAndUpdateRLDParams {
    wom_id: string;
    total_insurance_tax: number;
}

/** 工單退單折讓項目取消 (參數)
 * @param wocnd_id 工單折讓細項流水號
 * @param stock_id 庫存流水號(有填則異動庫存)
 * @param count 退回數量
 */
export interface WorkOrderCreditNoteCancelCreateParams {
    wocnd_id: string;
    stock_id: string;
    count: string;
}

/** 工單退單折讓取消項目查詢 (參數)
 * @param wocnd_id 工單折讓細項流水號 (可選)
 * @param wocnm_id 工單折讓單流水號 (可選)
 * @param rld_id 來源流水號 (維修清單) (可選)
 * @param stock_id 庫存流水號 (有填則異動庫存) (可選)
 * @param pl_id 付款記錄流水號 (可選)
 * @param commodity_code 商品編號 (可選)
 * @param commodity_name 商品名稱 (可選)
 * @param is_valid 是否失效 (可選)
 * @param is_before_complete  是否為結案前操作 (可選)
 * @param page 頁碼 (可選)
 * @param num_per_page 一頁幾筆 (可選)
 */
export interface WorkOrderCreditNoteCancelSearchParams {
    wocnd_id?: string;
    wocnm_id?: string;
    rld_id?: string;
    stock_id?: string;
    pl_id?: string;
    commodity_code?: string;
    commodity_name?: string;
    is_valid?: string;
    is_before_complete?: string;
    page?: number;
    num_per_page?: number;
}

/** 工單退單折讓取消項目查詢 (回傳) */
export interface WorkOrderCreditNoteCancelSearchResItem {
    basic_count: number;
    basic_unit_name: string;
    ccad_id_1: string;
    ccad_id_2: string;
    ccad_name_1: string;
    ccad_name_2: string;
    commodity_code: string;
    commodity_id: string;
    commodity_name: string;
    convert_count: number;
    convert_unit_name: string;
    count: number;
    cre_time: string; // ISO 8601 format
    cre_userid: string;
    inv_type: string;
    inv_type_name: string;
    invoice_number: string;
    master_type: string;
    name: string;
    note: string;
    pl_id: string;
    price: number;
    rld_code: string;
    rld_id: string;
    rld_name: string;
    rld_type: string;
    stock_id: string;
    total_price: number;
    uc_id: string;
    usercre_time: string; // ISO 8601 format
    warehouse_id: string;
    warehouse_name: string;
    warehouse_storage_space: string;
    wocnd_id: number;
    wocnm_id: string;
}

/** 工單庫存領用查詢參數
 * @param wom_id 工單流水號
 * @param commodity_id 商品流水號
 * @param commodity_code 商品編號
 * @param commodity_name 商品名稱
 * @param cre_time_start 進廠時間起
 * @param cre_time_end 進廠時間迄
 * @param sort_order 領用數量排序順序(正/倒)
 * @param page 頁碼
 * @param num_per_page 一頁幾筆
 * @param excel 是否匯出
 */
export interface WorkOrderStockRequisitionCommoditySearchParams {
    wom_id?: string;
    commodity_id?: string;
    commodity_code?: string;
    commodity_name?: string;
    cre_time_start?: string;
    cre_time_end?: string;
    sort_order?: string;
    page?: number;
    num_per_page?: number;
    excel?: string;
}

/** 工單庫存領用查詢結果 
 * @param commodity_code 零件編號
 * @param commodity_id 零件流水號
 * @param commodity_name 零件名稱
 * @param total_count 領用數量
*/
export interface WorkOrderStockRequisitionCommoditySearchResItem {
    commodity_code: string;
    commodity_id: string;
    commodity_name: string;
    total_count: number;
}

/** 工單維修狀態LOG創建用參數
 * @param maintain_status 維修狀態
 * @param wom_id 工單流水號
 */
export interface WorkOrderMaintainStatusLogCreateParams {
    maintain_status: string;
    wom_id: string;
}

/** 工單維修狀態LOG修改用參數
 * @param maintain_status 維修狀態
 * @param wom_id 工單流水號
 * @param womsl_id 工單預計交車日期LOG流水號
 */
export interface WorkOrderMaintainStatusLogUpdateParams {
    womsl_id: string;
    maintain_status: string;
    wom_id: string;
}

/** 工單維修狀態LOG查詢用參數
 * @param maintain_status 維修狀態
 * @param wom_id 工單流水號
 */
export interface WorkOrderMaintainStatusLogSearchParams {
    maintain_status?: string;
    wom_id: string;
}

/** 工單維修狀態LOG (回傳)
 * @param cre_time 創建時間
 * @param cre_userid 創建者ID
 * @param user_name 創建者名稱
 * @param upd_time 更新時間
 * @param upd_userid 更新者ID
 * @param womsl_id 工單維修狀態LOG流水號
 * @param wom_id 工單流水號
 * @param maintain_status 維修狀態
 */
export interface WorkOrderMaintainStatusLogSearchResItem {
    cre_time: string;
    cre_userid: string;
    user_name: string;
    upd_time: string;
    upd_userid: string;
    womsl_id: string;
    wom_id: string;
    maintain_status: string;
}

/** 工單預計交車時間LOG創建用參數
 * @param predict_delivery_datetime 預計交車日期
 * @param wom_id 工單流水號
 */
export interface WorkOrderPredictDeliveryLogCreateParams {
    predict_delivery_datetime: string;
    wom_id: string;
}

/** 工單預計交車時間LOG修改用參數
 * @param predict_delivery_datetime 預計交車日期
 * @param wom_id 工單流水號
 * @param womsl_id 工單預計交車日期LOG流水號
 */
export interface WorkOrderPredictDeliveryLogUpdateParams {
    womsl_id: string;
    predict_delivery_datetime: string;
    wom_id: string;
}

/** 工單預計交車時間LOG查詢用參數
 * @param predict_delivery_datetime 預計交車日期
 * @param wom_id 工單流水號
 */
export interface WorkOrderPredictDeliveryLogSearchParams {
    predict_delivery_datetime?: string;
    wom_id: string;
}

/** 工單預計交車時間LOG (回傳)
 * @param cre_time 創建時間
 * @param cre_userid 創建者ID
 * @param cre_user_username 創建者使用者名稱
 * @param cre_user_name 創建者名稱
 * @param upd_time 更新時間
 * @param upd_userid 更新者ID
 * @param wopdl_id 工單預計交車時間LOG流水號
 * @param wom_id 工單流水號
 * @param predict_delivery_datetime 預計交車日期
 */
export interface WorkOrderPredictDeliveryLogSearchResItem {
    cre_time: string;
    cre_userid: string;
    cre_user_username: string;
    cre_user_name: string;
    upd_time: string;
    upd_userid: string;
    wopdl_id: string;
    wom_id: string;
    predict_delivery_datetime: string;
}


/** 工單暫停用參數
 * @param wom_id 工單流水號
 */
export interface WorkOrderMasterTempStopParams {
    wom_id: string;
}

/** 工單暫停取消用參數
 * @param wom_id 工單流水號
 */
export interface WorkOrderMasterTempStopCancelParams {
    wom_id: string;
}